import { formatPhoneNumber as formatPhone } from "../lib/phone-format";
import {
  TechLineAccess,
  type TechLineParticipant,
  type TechLineAccessProps,
} from "../components/TechLineAccess";
import {
  ServiceAreasEditor,
  type ServiceAreaForm,
} from "../components/ServiceAreasEditor";
import {
  createContext,
  FormEvent,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CalendarDays,
  CreditCard,
  Mail,
  MapPin,
  Settings2,
  Users,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PurchaseOrderTypeSettings } from "../components/PurchaseOrderTypeSettings";
import { MembershipSettingsPanel } from "../components/MembershipSettingsPanel";
import { API_URL, apiFetch } from "../lib/api-client";
import { authClient } from "../lib/auth-client";
import { inviteOfficeUser, updateOfficeUserActive } from "../lib/office-users";

export type CompanyData = {
  company: {
    id: string;
    name: string;
    country: string | null;
    taxNumber: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    phone: string | null;
    timezone: string | null;
    afterHoursStartsAt: string;
    afterHoursEndsAt: string;
    materialMarkupBps: number | null;
    jobNumberDisplayOffset?: number;
    estimateNumberPrefix?: string;
    estimateNumberNext?: number | null;
    logoAttachmentId?: string | null;
    logoAvailability?: "available" | "not_copied";
  } | null;
  profile: {
    legalName: string | null;
    email: string;
    businessTypes: string;
    emergencyServiceAvailable: number;
    defaultBookingDurationMinutes?: number;
    bookingHorizonDays?: number;
    earliestBookingStart?: string;
    latestBookingStart?: string;
    lunchStartsAt?: string | null;
    lunchEndsAt?: string | null;
  } | null;
  businessHours: Array<{
    dayOfWeek: number;
    isClosed: number;
    opensAt: string | null;
    closesAt: string | null;
  }>;
  serviceAreas: Array<{
    id: string;
    name: string;
    active: boolean;
    color?: string;
    cities?: string[];
    state?: string | null;
    postalCodes?: string[];
  }>;
  team: Array<{
    id: string;
    name: string;
    email: string;
    phone: string | null;
    isOwner: boolean;
    hasLogin: boolean;
    active: boolean;
    role: string;
    canSendPurchaseOrders?: boolean | number;
    isTechnician: boolean;
    dispatchable: boolean;
    technicianActive: boolean;
    photoAttachmentId?: string | null;
    photoAvailability?: "available" | "not_copied";
    skills?: Array<{ id: string; name: string }>;
  }>;
  roles: Array<{ id: string; name: string }>;
  skills: Array<{ id: string; name: string; active: boolean }>;
  // Default duration needs a coworker D1 change: job_type has no duration
  // column, so the settings UI must not pretend that value is persisted.
  jobTypes: Array<{
    id: string;
    name: string;
    active: boolean;
    afterHoursServiceMultiplierBps: number | null;
    requiredSkillIds: string[];
    laborBillingEnabled?: boolean;
    laborPriceBookItemId?: string | null;
    laborMinimumMinutes?: number | null;
    laborRoundingIncrementMinutes?: number | null;
  }>;
  laborPricebookItems?: Array<{ id: string; name: string; priceCents: number }>;
  tags?: Array<{
    id: string;
    name: string;
    color: string | null;
    description: string | null;
    active: boolean;
  }>;
};

export type SettingsForm = {
  name: string;
  legalName: string;
  email: string;
  phone: string;
  country: string;
  taxNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  timezone: string;
  afterHoursStartsAt: string;
  afterHoursEndsAt: string;
  emergencyServiceAvailable: boolean;
  materialMarkupPercent: string;
  jobNumberDisplayOffset: string;
  estimateNumberPrefix: string;
  estimateNumberNext: string;
  defaultBookingDurationMinutes: number;
  bookingHorizonDays: number;
  earliestBookingStart: string;
  latestBookingStart: string;
  lunchStartsAt: string;
  lunchEndsAt: string;
  jobTypeMultipliers: Array<{
    id: string;
    name: string;
    active: boolean;
    multiplier: string;
  }>;
  businessHours: Array<{
    dayOfWeek: number;
    isClosed: boolean;
    opensAt: string | null;
    closesAt: string | null;
  }>;
  serviceAreas: ServiceAreaForm[];
};

type CompanyTab =
  "overview" | "email" | "operations" | "memberships" | "zones" | "team";

type QuickBooksStatus = {
  connected: boolean;
  reconnectRequired: boolean;
  status: "active" | "reconnect_required" | "disconnected";
  realmId: string | null;
  environment: string | null;
  connectedAt: string | null;
  pendingRealmId: string | null;
};

type QuickBooksAccountingSetup = {
  scan: {
    scannedAt: string;
    accounts: Array<{
      id: string;
      name: string;
      type: string | null;
      subtype: string | null;
    }>;
    paymentMethods: Array<{ id: string; name: string }>;
    vendors: Array<{ id: string; name: string }>;
    items: Array<{
      id: string;
      name: string;
      sku?: string | null;
      type: string | null;
      description: string | null;
      incomeAccountId: string | null;
    }>;
    undepositedFundsAccountId: string | null;
  } | null;
  mappings: Array<{ category: string; quickbooks_account_id: string }>;
  billMapping?: { apAccountId: string; expenseAccountId: string } | null;
  discountAccountId: string | null;
  paymentMethodMappings: Array<{
    workview_payment_method: string;
    quickbooks_payment_method_id: string;
  }>;
  sourceAccountMappings: Array<{
    accountKind: "income" | "expense";
    sourceAccountName: string;
    quickBooksAccountId: string;
  }>;
  sourceAccountNames: Array<{
    accountKind: "income" | "expense";
    sourceAccountName: string;
    itemCount: number;
  }>;
  accountingAccounts: Array<{
    id: string | null;
    accountKind: "income" | "expense";
    name: string;
    itemCount: number;
    origin: "imported" | "manual";
  }>;
  setupComplete: boolean;
  queuedManualPaymentCount: number;
};

function normalizeQuickBooksSetup(
  setup: QuickBooksAccountingSetup,
): QuickBooksAccountingSetup {
  const sourceAccountNames = Array.isArray(setup.sourceAccountNames)
    ? setup.sourceAccountNames
    : [];
  return {
    ...setup,
    mappings: Array.isArray(setup.mappings) ? setup.mappings : [],
    discountAccountId: setup.discountAccountId ?? null,
    paymentMethodMappings: Array.isArray(setup.paymentMethodMappings)
      ? setup.paymentMethodMappings
      : [],
    sourceAccountMappings: Array.isArray(setup.sourceAccountMappings)
      ? setup.sourceAccountMappings
      : [],
    sourceAccountNames,
    accountingAccounts: Array.isArray(setup.accountingAccounts)
      ? setup.accountingAccounts
      : sourceAccountNames.map((account) => ({
          id: null,
          accountKind: account.accountKind,
          name: account.sourceAccountName,
          itemCount: account.itemCount,
          origin: "imported" as const,
        })),
    scan: setup.scan
      ? {
          ...setup.scan,
          accounts: Array.isArray(setup.scan.accounts)
            ? setup.scan.accounts
            : [],
          paymentMethods: Array.isArray(setup.scan.paymentMethods)
            ? setup.scan.paymentMethods
            : [],
          items: Array.isArray(setup.scan.items) ? setup.scan.items : [],
          vendors: Array.isArray(setup.scan.vendors) ? setup.scan.vendors : [],
        }
      : null,
  };
}

const quickBooksMappingLabels: Array<{ category: string; label: string }> = [
  { category: "merchant_processing_fees", label: "Merchant processing fees" },
  { category: "financing_dealer_fees", label: "Financing / dealer fees" },
  {
    category: "warranty_vendor_recoveries",
    label: "Warranty / vendor recoveries",
  },
  { category: "rebates", label: "Vendor rebates (future)" },
  { category: "undeposited_funds", label: "Undeposited Funds" },
];

type AccountingAccount =
  QuickBooksAccountingSetup["accountingAccounts"][number];
type QuickBooksChartAccount = NonNullable<
  QuickBooksAccountingSetup["scan"]
>["accounts"][number];

function accountingAccountKey(
  account: Pick<AccountingAccount, "accountKind" | "name">,
) {
  return `${account.accountKind}:${account.name}`;
}

function normalizedAccountName(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function accountNameSimilarity(left: string, right: string) {
  const normalizedLeft = normalizedAccountName(left);
  const normalizedRight = normalizedAccountName(right);
  if (!normalizedLeft || !normalizedRight) return 0;
  if (normalizedLeft === normalizedRight) return 1;
  const leftWords = new Set(normalizedLeft.split(" "));
  const rightWords = new Set(normalizedRight.split(" "));
  const sharedWords = [...leftWords].filter((word) =>
    rightWords.has(word),
  ).length;
  return (2 * sharedWords) / (leftWords.size + rightWords.size);
}

export function autoMapAccountingAccounts(
  accountingAccounts: AccountingAccount[],
  quickBooksAccounts: QuickBooksChartAccount[],
  currentValues: Record<string, string>,
) {
  const nextValues = { ...currentValues };
  let mappedCount = 0;
  for (const account of accountingAccounts) {
    const key = accountingAccountKey(account);
    if (nextValues[key]) continue;
    const eligibleAccounts = quickBooksAccounts.filter((candidate) =>
      account.accountKind === "income"
        ? ["Income", "Other Income"].includes(candidate.type ?? "")
        : ["Expense", "Cost of Goods Sold", "Other Expense"].includes(
            candidate.type ?? "",
          ),
    );
    const matches = eligibleAccounts
      .map((candidate) => ({
        candidate,
        score: accountNameSimilarity(account.name, candidate.name),
      }))
      .filter(({ score }) => score >= 0.82)
      .sort((left, right) => right.score - left.score);
    const bestMatch = matches[0];
    const nextMatch = matches[1];
    if (
      bestMatch &&
      (!nextMatch || bestMatch.score - nextMatch.score >= 0.12)
    ) {
      nextValues[key] = bestMatch.candidate.id;
      mappedCount += 1;
    }
  }
  return { values: nextValues, mappedCount };
}

const paymentMethodLabels: Array<{ method: string; label: string }> = [
  { method: "cash", label: "Cash" },
  { method: "check", label: "Check" },
  { method: "credit_card", label: "Credit card (recorded manually)" },
  { method: "debit_card", label: "Debit card" },
  { method: "ach", label: "ACH / bank transfer" },
  { method: "greensky", label: "GreenSky" },
  { method: "synchrony", label: "Synchrony" },
  { method: "other", label: "Other" },
];

type StripeAccountStatus = {
  stripeAccountId: string;
  status: "pending" | "restricted" | "active" | "disabled";
  chargesEnabled: number | boolean;
  payoutsEnabled: number | boolean;
  requirements: { currently_due?: string[]; eventually_due?: string[] } | null;
  lastError: string | null;
};
type StripeTaxSettings = {
  enabled: boolean;
  defaultStripeTaxCode: string | null;
};
type EmailTemplate = {
  type:
    | "estimate_pdf"
    | "invoice_pdf"
    | "estimate_approval_request"
    | "estimate_approval_confirmation";
  subject: string;
  body: string;
  customized: boolean;
};
type EmailSettings = {
  addresses: string[];
  senders: Partial<Record<EmailWorkflow, string>>;
  customerReplyTo: string | null;
  officeConfirmationEmail: string | null;
  signature: string;
  poRepliesToEmployee: boolean;
  employeeReplyEmails: Record<string, string>;
};
type EmailSettingsResponse = {
  settings: EmailSettings;
  activatedAddresses: string[];
};
type VoiceBookingAgentSettings = {
  available: boolean;
  enabled: boolean;
  emergencyForwardTo: string | null;
  jobTypeIds: string[];
  recordingEnabled: boolean;
  transcriptEnabled: boolean;
  provisioningStatus?: "disabled" | "ready" | "failed";
  provisioningError?: string | null;
};
type TwilioConnectionStatus = {
  connected: boolean;
  editable: boolean;
  accountSid?: string;
  fromNumber?: string;
  officeForwardTo?: string | null;
  messagingServiceSid?: string | null;
  smsEnabled?: boolean;
  voiceEnabled?: boolean;
  automatedSmsEnabled?: boolean;
  onMyWaySms?: { enabled: boolean; template: string; sampleRecipientPhone: string | null };
  appointmentConfirmationSms?: { enabled: boolean; template: string; sampleRecipientPhone: string | null };
  suggestedOnMyWaySamplePhone?: string | null;
  techLine?: {
    phoneNumber: string;
    enabled: boolean;
    provisioningStatus: string;
    provisioningError: string | null;
  } | null;
  outboundCallRecipients?: Array<{ id: string; name: string; role: string; phoneNumber: string | null }>;
  techLineParticipants?: TechLineParticipant[];
  techLineDiagnostics?: { enabled: boolean; expiresAt: string | null };
  techLineDiagnosticsAvailable?: boolean;
  voiceBookingAgent?: VoiceBookingAgentSettings;
};

export type QuickBooksCallbackResult =
  "connected" | "switch_pending" | "denied" | "error";

export function parseQuickBooksCallback(
  value: string | null,
): QuickBooksCallbackResult | null {
  if (
    value === "connected" ||
    value === "switch_pending" ||
    value === "denied" ||
    value === "error"
  )
    return value;
  return null;
}

export function quickBooksCallbackMessage(
  result: QuickBooksCallbackResult,
): string {
  switch (result) {
    case "connected":
      return "QuickBooks Online is connected.";
    case "switch_pending":
      return "Review the QuickBooks company switch before it can take effect.";
    case "denied":
      return "QuickBooks authorization was cancelled.";
    case "error":
      return "QuickBooks could not be connected. Please try again.";
    default: {
      const _exhaustive: never = result;
      return _exhaustive;
    }
  }
}

type PricingReadiness = {
  jobsWithoutJobType: number;
  activePricebookItems: number;
  activeMaterialItems: number;
  materialsWithoutCost: number;
  zeroPriceItems: number;
  uncategorizedPricebookItems: number;
  businessHoursConfigured: boolean;
};
type JobSkillDefaultsPreview = {
  candidates: Array<{ jobTypeId: string; jobTypeName: string; count: number }>;
  exceptions: {
    noPrimaryType: number;
    inactivePrimaryType: number;
    noDefaults: number;
    existingRequirements: number;
    alreadySet: number;
  };
  warning: string;
};

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const timezones = [
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];
const tabs: Array<{ id: CompanyTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "email", label: "Email" },
  { id: "operations", label: "Operations" },
  { id: "memberships", label: "Memberships" },
  { id: "zones", label: "Zones" },
  { id: "team", label: "Team" },
];

export function formFromData(data: CompanyData): SettingsForm {
  const company = data.company!;
  return {
    name: company.name,
    legalName: data.profile?.legalName || "",
    email: data.profile?.email || "",
    phone: company.phone || "",
    country: company.country || "",
    taxNumber: company.taxNumber || "",
    addressLine1: company.addressLine1 || "",
    addressLine2: company.addressLine2 || "",
    city: company.city || "",
    state: company.state || "",
    postalCode: company.postalCode || "",
    timezone: company.timezone || "UTC",
    afterHoursStartsAt: company.afterHoursStartsAt || "17:00",
    afterHoursEndsAt: company.afterHoursEndsAt || "07:00",
    emergencyServiceAvailable: Boolean(data.profile?.emergencyServiceAvailable),
    materialMarkupPercent:
      company.materialMarkupBps == null
        ? ""
        : String(company.materialMarkupBps / 100),
    jobNumberDisplayOffset: String(company.jobNumberDisplayOffset || 0),
    estimateNumberPrefix: company.estimateNumberPrefix || "WV-EST-",
    estimateNumberNext: String(company.estimateNumberNext ?? 1),
    defaultBookingDurationMinutes:
      data.profile?.defaultBookingDurationMinutes ?? 120,
    bookingHorizonDays: data.profile?.bookingHorizonDays ?? 731,
    earliestBookingStart: data.profile?.earliestBookingStart ?? "07:00",
    latestBookingStart: data.profile?.latestBookingStart ?? "15:00",
    lunchStartsAt: data.profile?.lunchStartsAt ?? "12:00",
    lunchEndsAt: data.profile?.lunchEndsAt ?? "13:00",
    jobTypeMultipliers: (data.jobTypes ?? []).map((jobType) => ({
      id: jobType.id,
      name: jobType.name,
      active: jobType.active,
      multiplier:
        jobType.afterHoursServiceMultiplierBps === null
          ? ""
          : String(jobType.afterHoursServiceMultiplierBps / 10000),
    })),
    businessHours: Array.from({ length: 7 }, (_, dayOfWeek) => {
      const saved = data.businessHours.find(
        (item) => item.dayOfWeek === dayOfWeek,
      );
      const defaultOpen = dayOfWeek !== 0;
      return {
        dayOfWeek,
        isClosed: saved ? Boolean(saved.isClosed) : !defaultOpen,
        opensAt: saved?.opensAt || (defaultOpen ? "07:00" : null),
        closesAt: saved?.closesAt || (defaultOpen ? "17:00" : null),
      };
    }),
    serviceAreas: data.serviceAreas.map((area) => ({
      id: area.id,
      name: area.name,
      active: Boolean(area.active),
      color: area.color || "#2563eb",
      citiesText: (area.cities ?? []).join(", "),
      state: area.state || "",
      postalCodesText: (area.postalCodes ?? []).join(", "),
    })),
  };
}

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function TeamRow({
  member,
  busy = false,
  openProfile,
  openRoleEditor,
  setMemberActive,
  resendSetup,
  removeMember,
}: {
  member: CompanyData["team"][number];
  busy?: boolean;
  openProfile: (member: CompanyData["team"][number]) => void;
  openRoleEditor: (member: CompanyData["team"][number]) => void;
  setMemberActive: (
    member: CompanyData["team"][number],
    active: boolean,
  ) => void;
  resendSetup: (member: CompanyData["team"][number]) => void;
  removeMember: (member: CompanyData["team"][number]) => void;
}) {
  const offBoard =
    Boolean(member.isTechnician) &&
    !(member.dispatchable && member.technicianActive);
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-[var(--line)] p-3">
      <div className="flex min-w-0 items-center gap-3">
        {member.photoAttachmentId &&
        member.photoAvailability !== "not_copied" ? (
          <img
            src={`${API_URL}/api/v1/workspace/attachments/${member.photoAttachmentId}/content`}
            alt=""
            className="h-10 w-10 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <span
            title={
              member.photoAvailability === "not_copied"
                ? "Not copied to preview"
                : undefined
            }
            className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[var(--wash)] text-[13px] font-semibold text-[var(--ink-soft)]"
          >
            {initials(member.name)}
          </span>
        )}
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold text-[var(--ink)]">
            {member.name}
            {member.isOwner ? (
              <span className="rounded-lg bg-[var(--wash)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                Owner
              </span>
            ) : null}
            {offBoard ? (
              <span className="rounded-lg bg-[var(--accent-wash)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--accent)]">
                Not on the board
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 truncate text-[13px] text-[var(--ink-soft)]">
            {[
              member.active ? member.role : "Inactive",
              member.email,
              formatPhone(member.phone),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {member.isTechnician && (
            <p className="mt-1 text-[11px] text-[var(--ink-faint)]">
              {member.skills?.length
                ? member.skills.map((skill) => skill.name).join(" · ")
                : "No skills assigned"}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {member.isTechnician ? (
          <button
            type="button"
            onClick={() => openProfile(member)}
            className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)]"
          >
            Profile
          </button>
        ) : null}
        {member.isTechnician ? (
          <button
            type="button"
            onClick={() => resendSetup(member)}
            className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)]"
          >
            {member.hasLogin ? "Resend setup" : "Set up login"}
          </button>
        ) : null}
        {member.isOwner ? null : (
          <button
            type="button"
            onClick={() => openRoleEditor(member)}
            className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)]"
          >
            Role
          </button>
        )}
        {member.isOwner ? null : (
          <button
            type="button"
            disabled={busy}
            aria-busy={busy}
            onClick={() => setMemberActive(member, !member.active)}
            className="min-h-10 cursor-pointer rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-soft)] hover:bg-[var(--wash)] disabled:cursor-wait disabled:opacity-60"
          >
            {busy
              ? member.active
                ? "Deactivating…"
                : "Reactivating…"
              : member.active
                ? "Deactivate"
                : "Reactivate"}
          </button>
        )}
        {!member.isOwner && !member.active ? (
          <button
            type="button"
            onClick={() => removeMember(member)}
            className="rounded-full border border-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-[var(--accent)]"
          >
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}

const SettingsSelection = createContext<{
  selected: string | null;
  select: (id: string | null) => void;
}>({ selected: null, select: () => {} });
const panelDetails: Record<
  string,
  { description: string; icon: typeof Building2 }
> = {
  "Customer data": {
    description:
      "Imported customer records and access to your customer directory.",
    icon: Users,
  },
  "QuickBooks Online": {
    description:
      "Connect accounting, review mappings, and manage invoice and payment sync.",
    icon: CreditCard,
  },
  "Stripe payments": {
    description: "Manage your payment account and online payment connection.",
    icon: CreditCard,
  },
  "Stripe Tax": {
    description:
      "Configure sales tax, your head office, and tax registrations.",
    icon: CreditCard,
  },
  Twilio: {
    description:
      "Connect your company phone account and control SMS and call intake.",
    icon: CreditCard,
  },
  "Email identity": {
    description: "Review the logo and contact details customers see in email.",
    icon: Building2,
  },
  "Customize email": {
    description:
      "Preview company senders, replies, signatures, and confirmation recipients.",
    icon: Mail,
  },
  "Current email delivery": {
    description: "Review current senders, replies, and approval confirmations.",
    icon: Mail,
  },
  "Company logo": {
    description: "Upload or update the logo used for your company.",
    icon: Building2,
  },
  "Business information": {
    description:
      "Company name, legal name, contact information, and tax details.",
    icon: Building2,
  },
  "Business address": {
    description: "Manage your company's business address.",
    icon: MapPin,
  },
  "Operating settings": {
    description: "Timezone, emergency service, and workspace defaults.",
    icon: Settings2,
  },
  "Business hours": {
    description: "Set the working hours for each day of the week.",
    icon: CalendarDays,
  },
  "After-hours window": {
    description: "Choose when after-hours pricing starts and ends.",
    icon: CalendarDays,
  },
  "After-hours labor by job type": {
    description: "Set after-hours labor multipliers for each service type.",
    icon: CreditCard,
  },
  "Material markup": {
    description: "Set the default markup applied to material costs.",
    icon: CreditCard,
  },
  Zones: {
    description: "Manage dispatch zones, city coverage, and job-dot colors.",
    icon: MapPin,
  },
  "Current defaults": {
    description: "How company defaults are used across your workspace.",
    icon: Settings2,
  },
  "Dispatch booking window": {
    description:
      "Appointment duration, booking horizon, start times, and lunch.",
    icon: CalendarDays,
  },
  Team: {
    description: "Manage office users, technicians, roles, and invitations.",
    icon: Users,
  },
  Skills: {
    description: "Manage the skills used to match technicians with jobs.",
    icon: Users,
  },
  "Job number display": {
    description: "Choose how native WorkView job numbers appear.",
    icon: Settings2,
  },
  "Manage job types": {
    description: "Organize service types and their default required skills.",
    icon: Settings2,
  },
  "Timer-based labor billing": {
    description: "Configure hourly labor, minimums, and rounding by job type.",
    icon: CreditCard,
  },
  "Pricing data readiness": {
    description: "Review missing job types, costs, and pricing data.",
    icon: CreditCard,
  },
  Tags: {
    description: "Create and archive labels for organizing your records.",
    icon: Settings2,
  },
};

function Panel({
  title,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  const { selected, select } = useContext(SettingsSelection);
  const key = title.startsWith("Team (") ? "Team" : title;
  const open = selected === key;
  const [visited, setVisited] = useState(open);
  const heading = useRef<HTMLHeadingElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const id = useId();
  const details = panelDetails[key] ?? { description: "", icon: Settings2 };
  const Icon = details.icon;
  useEffect(() => {
    if (open) {
      setVisited(true);
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    } else if (wasOpen.current && !selected)
      trigger.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open, selected]);
  return (
    <>
      {!selected && (
        <button
          ref={trigger}
          type="button"
          aria-label={`Open ${title}`}
          aria-expanded={false}
          aria-controls={id}
          onClick={() => select(key)}
          className="flex min-h-44 flex-col items-start gap-3 rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5 text-left transition hover:bg-[var(--wash)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ink)]"
        >
          <Icon size={21} aria-hidden="true" />
          <span className="text-base font-semibold">{title}</span>
          {details.description && (
            <span className="text-sm leading-6 text-[var(--ink-soft)]">
              {details.description}
            </span>
          )}
          <span className="mt-auto inline-flex items-center gap-2 text-xs font-semibold">
            Open settings <ArrowUpRight size={14} aria-hidden="true" />
          </span>
        </button>
      )}
      <section
        id={id}
        hidden={!open}
        style={{ display: open ? "block" : "none" }}
        aria-labelledby={`${id}-heading`}
        className="col-span-full rounded-xl border border-[var(--line)] bg-[var(--paper)] p-5 sm:p-6"
      >
        {open && (
          <>
            <button
              type="button"
              onClick={() => select(null)}
              className="mb-5 inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-[var(--ink-soft)]"
            >
              <ArrowLeft size={16} aria-hidden="true" />
              All settings
            </button>
            <h2
              ref={heading}
              tabIndex={-1}
              id={`${id}-heading`}
              className="mb-2 scroll-mt-28 text-xl font-semibold"
            >
              {title}
            </h2>
            {details.description && (
              <p className="mb-6 text-sm text-[var(--ink-soft)]">
                {details.description}
              </p>
            )}
          </>
        )}
        {(open || visited) && <div className="min-w-0">{children}</div>}
      </section>
    </>
  );
}

function QuickBooksPanel({
  status,
  unavailable,
  message,
  error,
  disconnecting,
  setup,
  scanning,
  savingSetup,
  savingBillMapping,
  onScan,
  onSaveSetup,
  onSaveBillMapping,
  onSaveSourceAccountMappings,
  onCreateAccountingAccount,
  onSaveCustomerDiscountAccount,
  onConnect,
  onDisconnect,
  onConfirmRealmSwitch,
  onCancelRealmSwitch,
}: {
  status: QuickBooksStatus | null;
  unavailable: boolean;
  message: string;
  error: string;
  disconnecting: boolean;
  setup: QuickBooksAccountingSetup | null;
  scanning: boolean;
  savingSetup: boolean;
  savingBillMapping: boolean;
  onScan: () => void;
  onSaveSetup: (
    mappings: Array<{ category: string; quickBooksAccountId: string }>,
  ) => void;
  onSaveBillMapping: (apAccountId: string, expenseAccountId: string) => void;
  onSaveSourceAccountMappings: (
    mappings: Array<{
      accountKind: "income" | "expense";
      sourceAccountName: string;
      quickBooksAccountId: string;
    }>,
  ) => void;
  onCreateAccountingAccount: (
    accountKind: "income" | "expense",
    name: string,
  ) => void;
  onSaveCustomerDiscountAccount: (accountId: string) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onConfirmRealmSwitch: () => void;
  onCancelRealmSwitch: () => void;
}) {
  const [mappingValues, setMappingValues] = useState<Record<string, string>>(
    {},
  );
  const [billAccountValues, setBillAccountValues] = useState({
    apAccountId: "",
    expenseAccountId: "",
  });
  const [sourceAccountValues, setSourceAccountValues] = useState<
    Record<string, string>
  >({});
  const [autoMapMessage, setAutoMapMessage] = useState("");
  const [customerDiscountAccountId, setCustomerDiscountAccountId] =
    useState("");
  const [settingsTab, setSettingsTab] = useState<
    "defaults" | "payments" | "imported"
  >("defaults");
  const scan = setup?.scan;
  useEffect(() => {
    if (!setup) return;
    setMappingValues(
      Object.fromEntries(
        setup.mappings.map((mapping) => [
          mapping.category,
          mapping.quickbooks_account_id,
        ]),
      ),
    );
    setBillAccountValues(
      setup.billMapping ?? { apAccountId: "", expenseAccountId: "" },
    );
    setSourceAccountValues(
      Object.fromEntries(
        (setup.sourceAccountMappings ?? []).map((mapping) => [
          `${mapping.accountKind}:${mapping.sourceAccountName}`,
          mapping.quickBooksAccountId,
        ]),
      ),
    );
    setCustomerDiscountAccountId(setup.discountAccountId ?? "");
  }, [setup]);
  return (
    <Panel title="QuickBooks Online">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-xl text-sm text-[var(--ink-soft)]">
          Connect the accounting company used for invoice and payment sync.
        </p>
        {!unavailable && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onConnect}
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
            >
              {status?.connected
                ? "Reconnect"
                : status?.reconnectRequired
                  ? "Reconnect QuickBooks"
                  : "Connect QuickBooks"}
            </button>
            {status?.connected && (
              <button
                type="button"
                onClick={onDisconnect}
                disabled={disconnecting}
                className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] disabled:opacity-60"
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            )}
          </div>
        )}
      </div>
      {unavailable && (
        <p className="mt-3 text-sm text-[var(--ink-faint)]">
          QuickBooks is not available in this environment.
        </p>
      )}
      {status?.reconnectRequired && !unavailable && (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] px-3 py-2 text-sm text-[var(--accent)]"
        >
          QuickBooks authorization expired or was revoked. Sync is paused until
          an owner or administrator reconnects QuickBooks.
        </p>
      )}
      {status?.pendingRealmId && !unavailable && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] p-3 text-sm text-[var(--ink)]"
        >
          <p className="font-semibold">Switch QuickBooks company?</p>
          <p className="mt-1">
            Confirming changes the active company from{" "}
            {status.realmId ?? "the current company"} to {status.pendingRealmId}
            . Existing QuickBooks mappings and queued work will be archived,
            then you must scan and save new accounting mappings.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onConfirmRealmSwitch}
              className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
            >
              Confirm company switch
            </button>
            <button
              type="button"
              onClick={onCancelRealmSwitch}
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
            >
              Keep current company
            </button>
          </div>
        </div>
      )}
      {status?.connected && !unavailable && (
        <>
          <p className="mt-3 text-sm text-[var(--ink)]">
            Connected{status.environment ? ` · ${status.environment}` : ""}
            {status.realmId ? ` · company ${status.realmId}` : ""}
          </p>
          <div className="mt-4 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">
                  QuickBooks settings
                </p>
                <p className="mt-1 text-xs text-[var(--ink-soft)]">
                  Scan accounts, payment methods, and products or services from
                  your connected QuickBooks company.
                </p>
              </div>
              <button
                type="button"
                onClick={onScan}
                disabled={scanning}
                className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
              >
                {scanning
                  ? "Scanning…"
                  : setup?.scan
                    ? "Rescan QuickBooks"
                    : "Run setup scan"}
              </button>
            </div>
            {scan && (
              <div
                role="tablist"
                aria-label="QuickBooks settings"
                className="mt-4 flex gap-1 overflow-x-auto border-b border-[var(--line)]"
              >
                {(
                  [
                    { id: "defaults", label: "Accounts" },
                    { id: "payments", label: "Payment methods" },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.id}
                    id={`quickbooks-${tab.id}-tab`}
                    type="button"
                    role="tab"
                    aria-selected={settingsTab === tab.id}
                    aria-controls="quickbooks-settings-panel"
                    onClick={() => setSettingsTab(tab.id)}
                    className={`shrink-0 border-b-2 px-3 py-2.5 text-sm font-semibold ${settingsTab === tab.id ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            {scan && (
              <div
                id="quickbooks-settings-panel"
                role="tabpanel"
                aria-labelledby={`quickbooks-${settingsTab}-tab`}
              >
                {settingsTab === "defaults" && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-[var(--ink)]">
                        Accounting accounts
                      </p>
                      <p className="mt-1 text-xs text-[var(--ink-soft)]">
                        Pricebook items choose these income and expense
                        accounts. Map each one to its matching QuickBooks
                        account.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[var(--ink)]">
                          Map imported accounts
                        </p>
                        <p className="mt-1 text-xs text-[var(--ink-soft)]">
                          Auto-map finds clear name matches. Accounts without a
                          high-confidence match stay marked for review.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const result = autoMapAccountingAccounts(
                            setup.accountingAccounts,
                            scan.accounts,
                            sourceAccountValues,
                          );
                          setSourceAccountValues(result.values);
                          const remaining = setup.accountingAccounts.filter(
                            (account) =>
                              !result.values[accountingAccountKey(account)],
                          ).length;
                          setAutoMapMessage(
                            result.mappedCount
                              ? `Auto-mapped ${result.mappedCount} account${result.mappedCount === 1 ? "" : "s"}. ${remaining} still need${remaining === 1 ? "s" : ""} mapping.`
                              : "No additional high-confidence matches were found.",
                          );
                        }}
                        className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
                      >
                        Auto-map accounts
                      </button>
                    </div>
                    {autoMapMessage && (
                      <p
                        role="status"
                        className="text-xs text-[var(--ink-soft)]"
                      >
                        {autoMapMessage} Save accounting accounts to apply the
                        changes.
                      </p>
                    )}
                    <div className="max-h-80 space-y-2 overflow-y-auto">
                      {setup.accountingAccounts.map((account) => {
                        const key = accountingAccountKey(account);
                        const isUnmapped = !sourceAccountValues[key];
                        return (
                          <label
                            key={key}
                            className={`grid gap-2 rounded-lg border p-2 text-xs sm:grid-cols-2 sm:items-center ${isUnmapped ? "border-amber-200 bg-amber-50/40" : "border-[var(--line)]"}`}
                          >
                            <span className="flex items-center gap-1.5">
                              <strong>{account.name}</strong> ·{" "}
                              {account.accountKind === "income"
                                ? "Income"
                                : "Expense / COGS"}
                              {account.itemCount
                                ? ` · ${account.itemCount} item${account.itemCount === 1 ? "" : "s"}`
                                : ""}
                              {isUnmapped && (
                                <span
                                  aria-label="Needs QuickBooks mapping"
                                  title="Needs QuickBooks mapping"
                                  className="text-sm leading-none text-amber-700"
                                >
                                  ⚠
                                </span>
                              )}
                            </span>
                            <select
                              aria-label={`QuickBooks ${account.accountKind} account for ${account.name}`}
                              value={sourceAccountValues[key] ?? ""}
                              onChange={(event) =>
                                setSourceAccountValues((current) => ({
                                  ...current,
                                  [key]: event.target.value,
                                }))
                              }
                              className="w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm"
                            >
                              <option value="">Needs mapping</option>
                              {scan.accounts
                                .filter((candidate) =>
                                  account.accountKind === "income"
                                    ? ["Income", "Other Income"].includes(
                                        candidate.type ?? "",
                                      )
                                    : [
                                        "Expense",
                                        "Cost of Goods Sold",
                                        "Other Expense",
                                      ].includes(candidate.type ?? ""),
                                )
                                .map((candidate) => (
                                  <option
                                    key={candidate.id}
                                    value={candidate.id}
                                  >
                                    {candidate.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                    {!setup.accountingAccounts.length && (
                      <p className="text-sm text-[var(--ink-soft)]">
                        No accounting accounts yet. Add one below, or import
                        them from ServiceTitan.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        onSaveSourceAccountMappings(
                          setup.accountingAccounts.map((account) => ({
                            accountKind: account.accountKind,
                            sourceAccountName: account.name,
                            quickBooksAccountId:
                              sourceAccountValues[
                                `${account.accountKind}:${account.name}`
                              ] ?? "",
                          })),
                        )
                      }
                      className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
                    >
                      Save accounting accounts
                    </button>
                    <section className="border-t border-[var(--line)] pt-4">
                      <h3 className="text-sm font-semibold text-[var(--ink)]">
                        Add accounting account
                      </h3>
                      <div className="mt-3 flex flex-wrap items-end gap-2">
                        <label className="text-xs font-semibold text-[var(--ink-soft)]">
                          Type
                          <select
                            name="accountKind"
                            className="mt-1 block rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm text-[var(--ink)]"
                          >
                            <option value="income">Income</option>
                            <option value="expense">Expense / COGS</option>
                          </select>
                        </label>
                        <label className="min-w-52 flex-1 text-xs font-semibold text-[var(--ink-soft)]">
                          Account name
                          <input
                            required
                            name="accountName"
                            placeholder="For example, Sales"
                            className="mt-1 block w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm text-[var(--ink)]"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={(event) => {
                            const container = event.currentTarget.parentElement;
                            const kind =
                              container?.querySelector<HTMLSelectElement>(
                                "select[name=accountKind]",
                              )?.value;
                            const input =
                              container?.querySelector<HTMLInputElement>(
                                "input[name=accountName]",
                              );
                            const name = input?.value.trim();
                            if (
                              input &&
                              name &&
                              (kind === "income" || kind === "expense")
                            ) {
                              onCreateAccountingAccount(kind, name);
                              input.value = "";
                            }
                          }}
                          className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
                        >
                          Add account
                        </button>
                      </div>
                    </section>
                    <div className="border-t border-[var(--line)] pt-4">
                      <p className="text-sm font-semibold text-[var(--ink)]">
                        Transaction defaults
                      </p>
                      <p className="mt-1 text-xs text-[var(--ink-soft)]">
                        These apply to workflows that are not selected from a
                        Pricebook item.
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {quickBooksMappingLabels.map(({ category, label }) => (
                          <label key={category} className="block">
                            <span className="text-xs font-semibold text-[var(--ink-soft)]">
                              {label}
                            </span>
                            <select
                              value={
                                mappingValues[category] ??
                                (category === "undeposited_funds"
                                  ? (scan.undepositedFundsAccountId ?? "")
                                  : "")
                              }
                              onChange={(event) =>
                                setMappingValues((current) => ({
                                  ...current,
                                  [category]: event.target.value,
                                }))
                              }
                              className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-sm"
                            >
                              <option value="">Select an account</option>
                              {scan.accounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.name}
                                  {account.type ? ` · ${account.type}` : ""}
                                </option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        disabled={savingSetup}
                        onClick={() =>
                          onSaveSetup(
                            quickBooksMappingLabels
                              .map(({ category }) => ({
                                category,
                                quickBooksAccountId:
                                  mappingValues[category] ??
                                  (category === "undeposited_funds"
                                    ? (scan.undepositedFundsAccountId ?? "")
                                    : ""),
                              }))
                              .filter((mapping) => mapping.quickBooksAccountId),
                          )
                        }
                        className="mt-3 rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
                      >
                        {savingSetup ? "Saving…" : "Save transaction defaults"}
                      </button>
                    </div>
                  </div>
                )}
                {settingsTab === "payments" && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-[var(--ink)]">
                      Payment methods
                    </p>
                    <p className="mt-1 text-xs text-[var(--ink-soft)]">
                      Payment-method controls will be available here when they
                      are connected to company settings.
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {paymentMethodLabels.map(({ method, label }) => (
                        <label
                          key={method}
                          className="flex items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm text-[var(--ink-soft)]"
                        >
                          <input
                            type="checkbox"
                            checked
                            disabled
                            aria-label={`${label} payment method`}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {settingsTab === "defaults" &&
                  setup.billMapping !== undefined && (
                    <div className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-4">
                      <p className="text-sm font-semibold text-[var(--ink)]">
                        Purchase order bills
                      </p>
                      <p className="mt-1 text-xs text-[var(--ink-soft)]">
                        Sent purchase orders become QuickBooks bills. Choose the
                        payable and fallback expense accounts.
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="text-xs font-semibold">
                          Accounts Payable
                          <select
                            aria-label="QuickBooks Accounts Payable"
                            value={billAccountValues.apAccountId}
                            onChange={(event) =>
                              setBillAccountValues((current) => ({
                                ...current,
                                apAccountId: event.target.value,
                              }))
                            }
                            className="mt-1 block w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-sm"
                          >
                            <option value="">Select an account</option>
                            {scan.accounts
                              .filter(
                                (account) =>
                                  account.type === "Accounts Payable",
                              )
                              .map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.name}
                                </option>
                              ))}
                          </select>
                        </label>
                        <label className="text-xs font-semibold">
                          Purchase expense
                          <select
                            aria-label="QuickBooks purchase expense"
                            value={billAccountValues.expenseAccountId}
                            onChange={(event) =>
                              setBillAccountValues((current) => ({
                                ...current,
                                expenseAccountId: event.target.value,
                              }))
                            }
                            className="mt-1 block w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-sm"
                          >
                            <option value="">Select an account</option>
                            {scan.accounts
                              .filter((account) =>
                                [
                                  "Expense",
                                  "Cost of Goods Sold",
                                  "Other Expense",
                                ].includes(account.type ?? ""),
                              )
                              .map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.name} · {account.type}
                                </option>
                              ))}
                          </select>
                        </label>
                      </div>
                      <button
                        type="button"
                        disabled={
                          savingBillMapping ||
                          !billAccountValues.apAccountId ||
                          !billAccountValues.expenseAccountId
                        }
                        onClick={() =>
                          onSaveBillMapping(
                            billAccountValues.apAccountId,
                            billAccountValues.expenseAccountId,
                          )
                        }
                        className="mt-3 rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
                      >
                        {savingBillMapping ? "Saving…" : "Save bill accounts"}
                      </button>
                    </div>
                  )}
                {settingsTab === "defaults" && (
                  <div className="mt-4 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-4">
                    <p className="text-sm font-semibold text-[var(--ink)]">
                      Customer discounts
                    </p>
                    <p className="mt-1 text-xs text-[var(--ink-soft)]">
                      Choose the account for discounts and rebates given on a
                      customer invoice. Vendor rebates received later use a
                      separate workflow.
                    </p>
                    <select
                      aria-label="QuickBooks customer discount account"
                      value={customerDiscountAccountId}
                      onChange={(event) =>
                        setCustomerDiscountAccountId(event.target.value)
                      }
                      className="mt-3 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-sm"
                    >
                      <option value="">Select an account</option>
                      {scan.accounts
                        .filter((account) =>
                          ["Income", "Other Income"].includes(
                            account.type ?? "",
                          ),
                        )
                        .map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name} · {account.type}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      disabled={!customerDiscountAccountId}
                      onClick={() =>
                        onSaveCustomerDiscountAccount(customerDiscountAccountId)
                      }
                      className="mt-3 rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
                    >
                      Save discount account
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {message && (
        <p role="status" className="mt-3 text-sm text-[var(--ink)]">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-[var(--accent)]">
          {error}
        </p>
      )}
    </Panel>
  );
}

function StripePaymentsPanel({
  account,
  unavailable,
  busy,
  openingDashboard,
  disconnecting,
  error,
  onStart,
  onAdopt,
  onOpenDashboard,
  onDisconnect,
}: {
  account: StripeAccountStatus | null;
  unavailable: boolean;
  busy: boolean;
  openingDashboard: boolean;
  disconnecting: boolean;
  error: string;
  onStart: () => void;
  onAdopt: (accountId: string) => void;
  onOpenDashboard: () => void;
  onDisconnect: () => void;
}) {
  const [existingAccountId, setExistingAccountId] = useState("");
  const due = account?.requirements?.currently_due?.length ?? 0;
  const ready = Boolean(account?.chargesEnabled);
  const status = unavailable
    ? "Stripe is not available in this environment."
    : ready
      ? "Ready to take card payments."
      : account
        ? due
          ? `${due} item${due === 1 ? "" : "s"} still needed in Stripe.`
          : "Finish Stripe setup to take card payments."
        : "Not connected.";
  return (
    <Panel title="Stripe payments">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="text-sm text-[var(--ink-soft)]">
            Connect your business to securely accept card payments in WorkView
            Pro. Stripe collects and verifies the required business and bank
            details.
          </p>
          <p
            role="status"
            className="mt-3 text-sm font-medium text-[var(--ink)]"
          >
            {status}
          </p>
          {account?.lastError && (
            <p role="alert" className="mt-2 text-sm text-[var(--accent)]">
              {account.lastError}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-sm text-[var(--accent)]">
              {error}
            </p>
          )}
          {!unavailable && !ready && (
            <p className="mt-2 text-xs text-[var(--ink-faint)]">
              You will complete the secure setup in Stripe, then return here.
            </p>
          )}
        </div>
        {!unavailable && !account ? (
          <div className="w-full max-w-sm space-y-4">
            <button
              type="button"
              onClick={onStart}
              disabled={busy}
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
            >
              {busy ? "Opening Stripe…" : "Connect new Stripe account"}
            </button>
            <div className="border-t border-[var(--line)] pt-4">
              <p className="text-sm font-semibold text-[var(--ink)]">
                Or add an existing one
              </p>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="text-xs font-medium text-[var(--ink-soft)]">
                  Stripe account ID
                  <input
                    aria-label="Existing Stripe account ID"
                    value={existingAccountId}
                    onChange={(event) =>
                      setExistingAccountId(event.target.value)
                    }
                    placeholder="acct_…"
                    className="mt-1 block rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-sm text-[var(--ink)]"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => onAdopt(existingAccountId)}
                  disabled={busy || !existingAccountId.trim()}
                  className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
                >
                  Add existing account
                </button>
              </div>
            </div>
          </div>
        ) : (
          !unavailable && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onStart}
                disabled={busy}
                className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
              >
                {busy
                  ? "Opening Stripe…"
                  : ready
                    ? "Review setup"
                    : account
                      ? "Continue setup"
                      : "Connect Stripe"}
              </button>
              {ready && (
                <button
                  type="button"
                  onClick={onOpenDashboard}
                  disabled={busy || openingDashboard || disconnecting}
                  className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)] disabled:opacity-60"
                >
                  {openingDashboard
                    ? "Opening dashboard…"
                    : "Open Stripe Express dashboard"}
                </button>
              )}
              {account && (
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={busy || disconnecting}
                  className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] disabled:opacity-60"
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect Stripe"}
                </button>
              )}
            </div>
          )
        )}
      </div>
    </Panel>
  );
}

function StripeTaxPanel({
  settings,
  accountReady,
  onSave,
}: {
  settings: StripeTaxSettings | null;
  accountReady: boolean;
  onSave: (settings: StripeTaxSettings) => Promise<void>;
}) {
  const [enabled, setEnabled] = useState(settings?.enabled ?? false);
  const [code, setCode] = useState(settings?.defaultStripeTaxCode ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [state, setState] = useState("NM");
  const [registration, setRegistration] = useState<{
    id: string;
    state: string;
    status: string;
  } | null>(null);
  useEffect(() => {
    setEnabled(settings?.enabled ?? false);
    setCode(settings?.defaultStripeTaxCode ?? "");
  }, [settings]);
  const request = (path: string, body?: unknown) => {
    setSaving(true);
    setError("");
    void apiFetch(path, {
      method: "POST",
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
      .then(async (r) => {
        const p = await r.json();
        if (!r.ok) throw new Error(p.error?.message);
        if (p.data?.registration) setRegistration(p.data.registration);
      })
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  };
  return (
    <Panel title="Stripe Tax">
      <p>Calculate tax from each job’s complete service address.</p>
      <label>
        <input
          type="checkbox"
          checked={enabled}
          disabled={!accountReady}
          onChange={(e) => setEnabled(e.target.checked)}
        />{" "}
        Use Stripe Tax
      </label>
      <label>
        Default Stripe tax code
        <input
          aria-label="Default Stripe tax code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>
      <button
        type="button"
        onClick={() =>
          void onSave({ enabled, defaultStripeTaxCode: code || null })
        }
      >
        Save Stripe Tax settings
      </button>
      <div>
        <p>Tax registrations</p>
        <button
          type="button"
          disabled={!accountReady || saving}
          onClick={() =>
            request(
              "/api/v1/workspace/company/payment-settings/stripe-tax/head-office",
            )
          }
        >
          Set tax head office
        </button>
        <select
          aria-label="Registration state"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          <option value="NM">New Mexico</option>
          <option value="TX">Texas</option>
          <option value="CA">California</option>
        </select>
        <button
          type="button"
          disabled={!accountReady || saving}
          onClick={() =>
            request(
              "/api/v1/workspace/company/payment-settings/stripe-tax/registrations",
              { country: "US", state, activeFrom: "now" },
            )
          }
        >
          Add registration
        </button>
        {registration && (
          <p>
            {registration.state} · {registration.status}
          </p>
        )}
      </div>
      {error && <p role="alert">{error}</p>}
    </Panel>
  );
}

function TwilioPanel({
  status,
  companyPhone,
  jobTypes,
  onSave,
  onSaveTechLine,
  onSaveTechLineDiagnostics,
  onLoadTechLineDiagnostics,
  onSaveVoiceBookingAgent,
  onSaveOnMyWaySms,
  onSendOnMyWaySample,
  onSaveAppointmentConfirmationSms,
  onSendAppointmentConfirmationSample,
  onSaveOutboundCallRecipient,
}: {
  status: TwilioConnectionStatus | null;
  companyPhone: string | null;
  jobTypes: CompanyData["jobTypes"];
  onSave: (body: Record<string, unknown>) => Promise<void>;
  onSaveTechLine: (body: {
    phoneNumber: string;
    enabled: boolean;
  }) => Promise<void>;
  onSaveTechLineDiagnostics: (body: { enabled: boolean }) => Promise<{ enabled: boolean; expiresAt: string | null }>;
  onLoadTechLineDiagnostics: () => Promise<Array<{ id: string; kind: string; transcript: string | null; detail: string | null; destinationName: string | null; createdAt: string; expiresAt: string }>>;
  onSaveVoiceBookingAgent: (body: VoiceBookingAgentSettings) => Promise<void>;
  onSaveOnMyWaySms: (body: { enabled: boolean; template: string; sampleRecipientPhone: string | null }) => Promise<void>;
  onSendOnMyWaySample: (body: { template: string; recipientPhone: string }) => Promise<{ fromNumber: string }>;
  onSaveAppointmentConfirmationSms: (body: { enabled: boolean; template: string; sampleRecipientPhone: string | null }) => Promise<void>;
	 onSendAppointmentConfirmationSample: (body: { template: string; recipientPhone: string }) => Promise<{ fromNumber: string }>;
  onSaveOutboundCallRecipient: (userId: string, phoneNumber: string | null) => Promise<void>;
}) {
  const [accountSid, setAccountSid] = useState(status?.accountSid ?? "");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState(
    status?.fromNumber ?? companyPhone ?? "",
  );
  const [officeForwardTo, setOfficeForwardTo] = useState(
    status?.officeForwardTo ?? "",
  );
  const [messagingServiceSid, setMessagingServiceSid] = useState(
    status?.messagingServiceSid ?? "",
  );
  const [smsEnabled, setSmsEnabled] = useState(Boolean(status?.smsEnabled));
  const [voiceEnabled, setVoiceEnabled] = useState(
    Boolean(status?.voiceEnabled),
  );
  const [techLineNumber, setTechLineNumber] = useState(
    status?.techLine?.phoneNumber ?? "",
  );
  const [techLineEnabled, setTechLineEnabled] = useState(
    Boolean(status?.techLine?.enabled),
  );
  const [saving, setSaving] = useState(false);
  const [savingTechLine, setSavingTechLine] = useState(false);
  const [outboundRecipients, setOutboundRecipients] = useState<Record<string, string>>(() => Object.fromEntries((status?.outboundCallRecipients ?? []).map((recipient) => [recipient.id, recipient.phoneNumber ?? ""])));
  const [savingOutboundRecipient, setSavingOutboundRecipient] = useState<string | null>(null);
  const [outboundRecipientError, setOutboundRecipientError] = useState("");
  const [error, setError] = useState("");
  const [techLineError, setTechLineError] = useState("");
  const [techLineDiagnosticsEnabled, setTechLineDiagnosticsEnabled] = useState(Boolean(status?.techLineDiagnostics?.enabled));
  const [savingTechLineDiagnostics, setSavingTechLineDiagnostics] = useState(false);
  const [diagnosticEvents, setDiagnosticEvents] = useState<Array<{ id: string; kind: string; transcript: string | null; detail: string | null; destinationName: string | null; createdAt: string; expiresAt: string }>>([]);
  const agent = status?.voiceBookingAgent;
  const [agentEnabled, setAgentEnabled] = useState(Boolean(agent?.enabled));
  const [guard, setGuard] = useState(agent?.emergencyForwardTo ?? "");
  const [agentTypes, setAgentTypes] = useState<string[]>(
    agent?.jobTypeIds ?? [],
  );
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [onMyWayEnabled, setOnMyWayEnabled] = useState(Boolean(status?.onMyWaySms?.enabled));
  const [onMyWayTemplate, setOnMyWayTemplate] = useState(status?.onMyWaySms?.template ?? "Hi, your technician {{technicianName}} from {{companyName}} is on the way to {{serviceLocation}}. Reply to this text or call us at {{officePhone}} with any questions.");
  const [samplePhone, setSamplePhone] = useState(status?.onMyWaySms?.sampleRecipientPhone ?? status?.suggestedOnMyWaySamplePhone ?? "");
  const [onMyWaySaving, setOnMyWaySaving] = useState(false);
  const [sampleSending, setSampleSending] = useState(false);
  const [onMyWayNotice, setOnMyWayNotice] = useState("");
  const [onMyWayError, setOnMyWayError] = useState("");
  const [appointmentConfirmationSmsEnabled, setAppointmentConfirmationSmsEnabled] = useState(status?.appointmentConfirmationSms?.enabled ?? true);
	const [appointmentConfirmationSmsTemplate, setAppointmentConfirmationSmsTemplate] = useState(status?.appointmentConfirmationSms?.template ?? "Hi, this is {{companyName}}. Your appointment is confirmed for {{appointmentDateTime}} at {{serviceLocation}}. Reply to this text or call us at {{officePhone}} with any questions.");
  const [appointmentConfirmationSamplePhone, setAppointmentConfirmationSamplePhone] = useState(status?.appointmentConfirmationSms?.sampleRecipientPhone ?? "");
  const [appointmentConfirmationSmsSaving, setAppointmentConfirmationSmsSaving] = useState(false);
  const [appointmentConfirmationSampleSending, setAppointmentConfirmationSampleSending] = useState(false);
  const [appointmentConfirmationSmsNotice, setAppointmentConfirmationSmsNotice] = useState("");
  const [appointmentConfirmationSmsError, setAppointmentConfirmationSmsError] = useState("");
  const voiceNeedsForwardingNumber = voiceEnabled && !officeForwardTo.trim();
  useEffect(() => {
    setAccountSid(status?.accountSid ?? "");
    setFromNumber(status?.fromNumber ?? companyPhone ?? "");
    setOfficeForwardTo(status?.officeForwardTo ?? "");
    setMessagingServiceSid(status?.messagingServiceSid ?? "");
    setSmsEnabled(Boolean(status?.smsEnabled));
    setVoiceEnabled(Boolean(status?.voiceEnabled));
    setTechLineNumber(status?.techLine?.phoneNumber ?? "");
    setTechLineEnabled(Boolean(status?.techLine?.enabled));
    setOutboundRecipients(Object.fromEntries((status?.outboundCallRecipients ?? []).map((recipient) => [recipient.id, recipient.phoneNumber ?? ""])));
    setTechLineDiagnosticsEnabled(Boolean(status?.techLineDiagnostics?.enabled));
    setOnMyWayEnabled(Boolean(status?.onMyWaySms?.enabled));
    setOnMyWayTemplate(status?.onMyWaySms?.template ?? "Hi, your technician {{technicianName}} from {{companyName}} is on the way to {{serviceLocation}}. Reply to this text or call us at {{officePhone}} with any questions.");
    setSamplePhone(status?.onMyWaySms?.sampleRecipientPhone ?? status?.suggestedOnMyWaySamplePhone ?? "");
    setAppointmentConfirmationSmsEnabled(status?.appointmentConfirmationSms?.enabled ?? true);
		setAppointmentConfirmationSmsTemplate(status?.appointmentConfirmationSms?.template ?? "Hi, this is {{companyName}}. Your appointment is confirmed for {{appointmentDateTime}} at {{serviceLocation}}. Reply to this text or call us at {{officePhone}} with any questions.");
    setAppointmentConfirmationSamplePhone(status?.appointmentConfirmationSms?.sampleRecipientPhone ?? "");
  }, [companyPhone, status]);
  useEffect(() => {
    setAgentEnabled(Boolean(agent?.enabled));
    setGuard(agent?.emergencyForwardTo ?? "");
    setAgentTypes(agent?.jobTypeIds ?? []);
  }, [agent]);
  if (status && !status.editable)
    return (
      <Panel title="Twilio">
        <p>
          Twilio is intentionally disabled in preview. Connect this company only
          in production; preview never sends customer texts or accepts live
          calls.
        </p>
      </Panel>
    );
  return (
    <Panel title="Twilio">
      <p className="mb-4 text-sm text-[var(--ink-soft)]">
        Connect this company’s own Twilio account. WorkView encrypts the token
        and never shows it again. The main Twilio number must match the company
        phone:{" "}
        <strong>
          {formatPhone(companyPhone) || "set a company phone first"}
        </strong>
        .
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium">
          Account SID
          <input
            aria-label="Twilio Account SID"
            value={accountSid}
            onChange={(event) => setAccountSid(event.target.value)}
            placeholder="AC…"
            className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium">
          Auth Token
          <input
            aria-label="Twilio Auth Token"
            value={authToken}
            onChange={(event) => setAuthToken(event.target.value)}
            type="password"
            autoComplete="new-password"
            placeholder={
              status?.connected
                ? "Enter a new token to change settings"
                : "Twilio Auth Token"
            }
            className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium">
          Main Twilio number
          <input
            aria-label="Twilio main number"
            value={fromNumber}
            onChange={(event) => setFromNumber(event.target.value)}
            placeholder="+15059174130"
            className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium">
          Office forwarding number
          <input
            aria-label="Twilio office forwarding number"
            value={officeForwardTo}
            onChange={(event) => setOfficeForwardTo(event.target.value)}
            placeholder="+1…"
            className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2"
          />
        </label>
      </div>
      <div className="mt-5 space-y-3">
        <label className="flex gap-3 text-sm">
          <input
            type="checkbox"
            checked={smsEnabled}
            onChange={(event) => setSmsEnabled(event.target.checked)}
          />
          Enable live SMS inbox and replies
        </label>
        <label className="flex gap-3 text-sm">
          <input
            type="checkbox"
            checked={voiceEnabled}
            onChange={(event) => setVoiceEnabled(event.target.checked)}
          />
          Enable incoming calls to the office
        </label>
      </div>
      {voiceNeedsForwardingNumber && (
        <p role="alert" className="mt-3 text-sm text-[var(--accent)]">
          Enter the office forwarding number before enabling incoming calls.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-[var(--accent)]">
          {error}
        </p>
      )}
      <button
        type="button"
        disabled={
          saving ||
          !accountSid ||
          !authToken ||
          !fromNumber ||
          voiceNeedsForwardingNumber
        }
        onClick={() => {
          setSaving(true);
          setError("");
          void onSave({
            accountSid,
            authToken,
            fromNumber,
            officeForwardTo: officeForwardTo || null,
            messagingServiceSid: messagingServiceSid || null,
            smsEnabled,
            voiceEnabled,
            automatedSmsEnabled: false,
          })
            .catch((cause) =>
              setError(
                cause instanceof Error
                  ? cause.message
                  : "Unable to save Twilio connection.",
              ),
            )
            .finally(() => {
              setSaving(false);
              setAuthToken("");
            });
        }}
        className="mt-5 rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60"
      >
        {saving
          ? "Saving…"
          : status?.connected
            ? "Update Twilio connection"
            : "Connect Twilio"}
      </button>
      {agent?.available && (
        <div className="mt-8 border-t border-[var(--line)] pt-5">
          <h3 className="text-base font-semibold">After-Hours Booking Agent</h3>
          <p className="mt-1 text-sm text-[var(--ink-soft)]">
            Disabled by default. Enabling changes MGP’s Twilio voice webhook;
            disabling restores the prior webhook immediately.
          </p>
          {agent?.provisioningStatus === "failed" && (
            <p role="alert" className="mt-2 text-sm text-[var(--accent)]">
              {agent.provisioningError ||
                "Twilio routing needs attention before this agent can receive calls."}
            </p>
          )}
          <label className="mt-4 flex gap-3 text-sm">
            <input
              aria-label="Enable after-hours AI booking agent"
              type="checkbox"
              checked={agentEnabled}
              onChange={(event) => setAgentEnabled(event.target.checked)}
            />
            Enable after-hours AI booking agent
          </label>
          <label className="mt-3 block text-sm font-medium">
            Emergency forwarding number
            <input
              aria-label="Emergency forwarding number"
              value={guard}
              onChange={(event) => setGuard(event.target.value)}
              placeholder="+1…"
              className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2"
            />
          </label>
          <p className="mt-3 text-sm font-medium">
            Services the agent may book
          </p>
          {jobTypes
            .filter((type) => type.active)
            .map((type) => (
              <label className="mt-2 flex gap-2 text-sm" key={type.id}>
                <input
                  type="checkbox"
                  checked={agentTypes.includes(type.id)}
                  onChange={(event) =>
                    setAgentTypes((current) =>
                      event.target.checked
                        ? [...current, type.id]
                        : current.filter((id) => id !== type.id),
                    )
                  }
                />
                {type.name}
              </label>
            ))}
          {agentError && (
            <p role="alert" className="mt-3 text-sm text-[var(--accent)]">
              {agentError}
            </p>
          )}
          <button
            type="button"
            disabled={agentSaving || !status?.connected}
            onClick={() => {
              setAgentSaving(true);
              setAgentError("");
              void onSaveVoiceBookingAgent({
                available: true,
                enabled: agentEnabled,
                emergencyForwardTo: guard || null,
                jobTypeIds: agentTypes,
                recordingEnabled: true,
                transcriptEnabled: true,
              })
                .catch((cause) =>
                  setAgentError(
                    cause instanceof Error
                      ? cause.message
                      : "Unable to save booking agent settings.",
                  ),
                )
                .finally(() => setAgentSaving(false));
            }}
            className="mt-4 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {agentSaving ? "Saving…" : "Save booking agent"}
          </button>
        </div>
      )}
      <div className="mt-8 border-t border-[var(--line)] pt-5">
        <h3 className="text-base font-semibold">On-my-way text</h3>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Sent from the main MGP number. Customers can reply to that same number. Use the variables below; their values are filled from the dispatched visit.</p>
        <label className="mt-4 flex gap-3 text-sm"><input aria-label="Enable on-my-way text" type="checkbox" checked={onMyWayEnabled} disabled={!status?.connected || !smsEnabled} onChange={(event) => setOnMyWayEnabled(event.target.checked)} />Send when a technician marks a visit on the way</label>
        <label className="mt-3 block text-sm font-medium">Customer text template<textarea aria-label="On-my-way text template" value={onMyWayTemplate} onChange={(event) => setOnMyWayTemplate(event.target.value)} className="mt-1 block min-h-28 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label>
        <p className="mt-1 text-xs text-[var(--ink-soft)]">Variables: {'{{technicianName}}'}, {'{{companyName}}'}, {'{{serviceLocation}}'}, {'{{officePhone}}'}.</p>
        <label className="mt-4 block text-sm font-medium">Sample recipient<input aria-label="On-my-way sample recipient" value={samplePhone} onChange={(event) => setSamplePhone(event.target.value)} placeholder="+15054170351" className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label>
        {onMyWayError && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{onMyWayError}</p>}
        {onMyWayNotice && <p role="status" className="mt-3 text-sm">{onMyWayNotice}</p>}
        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" disabled={onMyWaySaving || !status?.connected || !smsEnabled || !onMyWayTemplate.trim()} onClick={() => { setOnMyWaySaving(true); setOnMyWayError(""); setOnMyWayNotice(""); void onSaveOnMyWaySms({ enabled: onMyWayEnabled, template: onMyWayTemplate, sampleRecipientPhone: samplePhone.trim() || null }).then(() => setOnMyWayNotice("On-my-way text settings saved.")).catch(cause => setOnMyWayError(cause instanceof Error ? cause.message : "Unable to save on-my-way text settings.")).finally(() => setOnMyWaySaving(false)); }} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60">{onMyWaySaving ? "Saving…" : "Save on-my-way text"}</button>
          <button type="button" disabled={sampleSending || !status?.connected || !smsEnabled || !onMyWayTemplate.trim() || !samplePhone.trim()} onClick={() => { setSampleSending(true); setOnMyWayError(""); setOnMyWayNotice(""); void onSendOnMyWaySample({ template: onMyWayTemplate, recipientPhone: samplePhone }).then(({ fromNumber }) => setOnMyWayNotice(`Sample sent from ${formatPhone(fromNumber)}.`)).catch(cause => setOnMyWayError(cause instanceof Error ? cause.message : "Unable to send sample text.")).finally(() => setSampleSending(false)); }} className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60">{sampleSending ? "Sending…" : "Send sample"}</button>
        </div>
      </div>
      <div className="mt-8 border-t border-[var(--line)] pt-5">
        <h3 className="text-base font-semibold">Appointment confirmation text</h3>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Newly booked appointments automatically text the customer from the main company number. Blocked or opted-out numbers are never texted, and each delivery is recorded in Communications.</p>
        <label className="mt-4 flex gap-3 text-sm"><input aria-label="Enable appointment confirmation text" type="checkbox" checked={appointmentConfirmationSmsEnabled} disabled={!status?.connected || !smsEnabled} onChange={(event) => setAppointmentConfirmationSmsEnabled(event.target.checked)} />Send a confirmation when an appointment is booked</label>
		<label className="mt-3 block text-sm font-medium">Customer text template<textarea aria-label="Appointment confirmation text template" value={appointmentConfirmationSmsTemplate} onChange={(event) => setAppointmentConfirmationSmsTemplate(event.target.value)} className="mt-1 block min-h-28 w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label>
		<p className="mt-1 text-xs text-[var(--ink-soft)]">Variables: {'{{companyName}}'}, {'{{appointmentDateTime}}'}, {'{{serviceLocation}}'}, {'{{officePhone}}'}.</p>
		<label className="mt-4 block text-sm font-medium">Sample recipient<input aria-label="Appointment confirmation sample recipient" value={appointmentConfirmationSamplePhone} onChange={(event) => setAppointmentConfirmationSamplePhone(event.target.value)} placeholder="+15054170351" className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2" /></label>
        {appointmentConfirmationSmsError && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{appointmentConfirmationSmsError}</p>}
		{appointmentConfirmationSmsNotice && <p role="status" className="mt-3 text-sm">{appointmentConfirmationSmsNotice}</p>}
		<div className="mt-4 flex flex-wrap gap-3"><button type="button" disabled={appointmentConfirmationSmsSaving || !status?.connected || !smsEnabled || !appointmentConfirmationSmsTemplate.trim()} onClick={() => { setAppointmentConfirmationSmsSaving(true); setAppointmentConfirmationSmsError(""); setAppointmentConfirmationSmsNotice(""); void onSaveAppointmentConfirmationSms({ enabled: appointmentConfirmationSmsEnabled, template: appointmentConfirmationSmsTemplate, sampleRecipientPhone: appointmentConfirmationSamplePhone.trim() || null }).then(() => setAppointmentConfirmationSmsNotice("Appointment confirmation text settings saved.")).catch(cause => setAppointmentConfirmationSmsError(cause instanceof Error ? cause.message : "Unable to save appointment confirmation settings.")).finally(() => setAppointmentConfirmationSmsSaving(false)); }} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60">{appointmentConfirmationSmsSaving ? "Saving…" : "Save appointment confirmation text"}</button>
		<button type="button" disabled={appointmentConfirmationSampleSending || !status?.connected || !smsEnabled || !appointmentConfirmationSmsTemplate.trim() || !appointmentConfirmationSamplePhone.trim()} onClick={() => { setAppointmentConfirmationSampleSending(true); setAppointmentConfirmationSmsError(""); setAppointmentConfirmationSmsNotice(""); void onSendAppointmentConfirmationSample({ template: appointmentConfirmationSmsTemplate, recipientPhone: appointmentConfirmationSamplePhone }).then(({ fromNumber }) => setAppointmentConfirmationSmsNotice(`Sample sent from ${formatPhone(fromNumber)}.`)).catch(cause => setAppointmentConfirmationSmsError(cause instanceof Error ? cause.message : "Unable to send sample text.")).finally(() => setAppointmentConfirmationSampleSending(false)); }} className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-60">{appointmentConfirmationSampleSending ? "Sending…" : "Send sample"}</button></div>
      </div>
      <div className="mt-8 border-t border-[var(--line)] pt-5">
        <h3 className="text-base font-semibold">Outbound customer calls</h3>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">When a team member clicks a customer call button, WorkView calls this number first, then connects the customer from the main company number. These can be ordinary mobile or desk numbers; they do not need to be Twilio numbers.</p>
        {!status?.outboundCallRecipients?.length ? <p className="mt-3 text-sm text-[var(--ink-soft)]">Add active office users before assigning outbound call numbers.</p> : <div className="mt-4 space-y-3">
          {status.outboundCallRecipients.map((recipient) => <div key={recipient.id} className="rounded-lg border border-[var(--line)] p-3">
            <label className="block text-sm font-medium">{recipient.name} <span className="font-normal text-[var(--ink-soft)]">· {recipient.role}</span>
              <input aria-label={`Outbound call number for ${recipient.name}`} type="tel" value={outboundRecipients[recipient.id] ?? ""} onChange={(event) => setOutboundRecipients((current) => ({ ...current, [recipient.id]: event.target.value }))} placeholder="+15052367923" disabled={!status?.connected || savingOutboundRecipient === recipient.id} className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2 disabled:opacity-60" />
            </label>
            <button type="button" disabled={!status?.connected || savingOutboundRecipient === recipient.id} onClick={() => { setSavingOutboundRecipient(recipient.id); setOutboundRecipientError(""); void onSaveOutboundCallRecipient(recipient.id, outboundRecipients[recipient.id]?.trim() || null).catch((cause) => setOutboundRecipientError(cause instanceof Error ? cause.message : "Unable to save the outbound call number.")).finally(() => setSavingOutboundRecipient(null)); }} className="mt-3 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60">{savingOutboundRecipient === recipient.id ? "Saving…" : "Save call number"}</button>
          </div>)}
        </div>}
        {outboundRecipientError && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{outboundRecipientError}</p>}
      </div>
      <div className="mt-8 border-t border-[var(--line)] pt-5">
        <h3 className="text-base font-semibold">Tech Line</h3>
        <label className="mt-4 block text-sm font-medium">
          Tech Line number
          <input
            aria-label="Twilio Tech Line number"
            value={techLineNumber}
            onChange={(event) => setTechLineNumber(event.target.value)}
            placeholder="+15055140227"
            disabled={!status?.connected}
            className="mt-1 block w-full rounded-lg border border-[var(--line)] px-3 py-2 disabled:opacity-60"
          />
        </label>
        <label className="mt-3 flex gap-3 text-sm">
          <input
            type="checkbox"
            checked={techLineEnabled}
            disabled={!status?.connected}
            onChange={(event) => setTechLineEnabled(event.target.checked)}
          />
          Enable recorded staff-to-staff connections
        </label>
        {techLineError && (
          <p role="alert" className="mt-3 text-sm text-[var(--accent)]">
            {techLineError}
          </p>
        )}
        <button
          type="button"
          disabled={
            savingTechLine || !status?.connected || !techLineNumber.trim()
          }
          onClick={() => {
            setSavingTechLine(true);
            setTechLineError("");
            void onSaveTechLine({
              phoneNumber: techLineNumber,
              enabled: techLineEnabled,
            })
              .catch((cause) =>
                setTechLineError(
                  cause instanceof Error
                    ? cause.message
                    : "Unable to save Tech Line settings.",
                ),
              )
              .finally(() => setSavingTechLine(false));
          }}
          className="mt-4 rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60"
        >
          {savingTechLine ? "Saving…" : "Save Tech Line"}
        </button>
        {status?.techLineDiagnosticsAvailable && <div className="mt-5 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-4 text-sm">
          <label className="flex gap-3 font-medium">
            <input aria-label="Enable temporary Tech Line diagnostics" type="checkbox" checked={techLineDiagnosticsEnabled} onChange={(event) => setTechLineDiagnosticsEnabled(event.target.checked)} />
            Temporarily log Tech Line matching diagnostics
          </label>
          <p className="mt-2 text-[var(--ink-soft)]">When enabled, WorkView saves the text Twilio already sends us plus the matching result for 24 hours. It does not call another AI or transcription provider. Callers hear a troubleshooting notice.</p>
          {status?.techLineDiagnostics?.enabled && status.techLineDiagnostics.expiresAt && <p className="mt-2 text-xs text-[var(--ink-soft)]">Auto-disables {new Date(status.techLineDiagnostics.expiresAt).toLocaleString()}.</p>}
          <div className="mt-3 flex flex-wrap gap-3">
            <button type="button" disabled={savingTechLineDiagnostics || !status?.connected} onClick={() => { setSavingTechLineDiagnostics(true); setTechLineError(""); void onSaveTechLineDiagnostics({ enabled: techLineDiagnosticsEnabled }).catch((cause) => setTechLineError(cause instanceof Error ? cause.message : "Unable to save diagnostics.")).finally(() => setSavingTechLineDiagnostics(false)); }} className="rounded-lg border border-[var(--line)] px-3 py-2 font-semibold disabled:opacity-60">{savingTechLineDiagnostics ? "Saving…" : "Save diagnostics"}</button>
            <button type="button" onClick={() => { setTechLineError(""); void onLoadTechLineDiagnostics().then(setDiagnosticEvents).catch((cause) => setTechLineError(cause instanceof Error ? cause.message : "Unable to load diagnostics.")); }} className="rounded-lg border border-[var(--line)] px-3 py-2 font-semibold">Load latest diagnostics</button>
          </div>
          {diagnosticEvents.length > 0 && <ul className="mt-3 space-y-2 text-xs">{diagnosticEvents.map((event) => <li key={event.id} className="rounded border border-[var(--line)] bg-[var(--paper)] p-2"><strong>{event.kind}</strong> · {new Date(event.createdAt).toLocaleString()} {event.destinationName ? `→ ${event.destinationName}` : ''}{event.transcript ? <div>Heard: {event.transcript}</div> : null}{event.detail ? <div>Detail: {event.detail}</div> : null}</li>)}</ul>}
        </div>}
      </div>
    </Panel>
  );
}

function TechLineParticipantPanel(props: TechLineAccessProps) {
  return (
    <Panel title="Tech Line access">
      <TechLineAccess {...props} />
    </Panel>
  );
}

function EmailIdentityPanel({
  company,
  form,
  onEdit,
}: {
  company: NonNullable<CompanyData["company"]>;
  form: SettingsForm;
  onEdit: (panel: string) => void;
}) {
  const address = [
    form.addressLine1,
    form.addressLine2,
    [form.city, form.state, form.postalCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const logoAvailable = Boolean(
    company.logoAttachmentId && company.logoAvailability !== "not_copied",
  );
  return (
    <Panel title="Email identity">
      <p className="mb-4 text-sm text-[var(--ink-soft)]">
        These company details are available for branded messages. Changes to the
        logo and business information are saved in Overview.
      </p>
      <div className="flex flex-wrap items-start gap-4 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-4">
        {logoAvailable ? (
          <img
            src={`${API_URL}/api/v1/workspace/attachments/${encodeURIComponent(company.logoAttachmentId!)}/content`}
            alt={`${company.name} logo`}
            className="h-16 w-16 rounded-lg bg-[var(--paper)] object-contain"
          />
        ) : (
          <span className="grid h-16 w-16 place-items-center rounded-lg bg-[var(--paper)] text-lg font-semibold">
            {initials(form.name)}
          </span>
        )}
        <div className="min-w-0 space-y-1 text-sm">
          <p className="font-semibold">{form.name || "Company name needed"}</p>
          <p>{form.email || "Business email needed"}</p>
          <p>{formatPhone(form.phone) || "Phone number not set"}</p>
          <p>{address || "Business address not set"}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onEdit("Company logo")}
          className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold"
        >
          Edit logo
        </button>
        <button
          type="button"
          onClick={() => onEdit("Business information")}
          className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold"
        >
          Edit contact details
        </button>
        <button
          type="button"
          onClick={() => onEdit("Business address")}
          className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold"
        >
          Edit address
        </button>
      </div>
      <p className="mt-4 text-xs text-[var(--ink-faint)]">
        These details are used in estimate, invoice, approval, and purchase
        order emails.
      </p>
    </Panel>
  );
}

const emailWorkflowLabels: Array<{
  type: EmailTemplate["type"] | "purchase_order";
  label: string;
}> = [
  { type: "estimate_pdf", label: "Estimate PDF" },
  { type: "estimate_approval_request", label: "Online estimate approval" },
  { type: "estimate_approval_confirmation", label: "Approval confirmation" },
  { type: "invoice_pdf", label: "Invoice" },
  { type: "purchase_order", label: "Purchase order" },
];
type EmailWorkflow = (typeof emailWorkflowLabels)[number]["type"];

function canSendPurchaseOrders(member: CompanyData["team"][number]) {
  return (
    Boolean(member.active) &&
    (member.canSendPurchaseOrders == null
      ? Boolean(member.isOwner) || member.role.toLowerCase() === "admin"
      : Boolean(member.canSendPurchaseOrders))
  );
}

function EmailCustomizationPanel({
  company,
  form,
  team,
  templates,
  emailSettings,
  onSaveTemplate,
  onSaveSettings,
}: {
  company: NonNullable<CompanyData["company"]>;
  form: SettingsForm;
  team: CompanyData["team"];
  templates: EmailTemplate[];
  emailSettings: EmailSettingsResponse | null;
  onSaveTemplate: (template: EmailTemplate) => Promise<void>;
  onSaveSettings: (settings: EmailSettings) => Promise<void>;
}) {
  const [addresses, setAddresses] = useState<string[]>(() =>
    form.email ? [form.email] : [],
  );
  const [newAddress, setNewAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [senders, setSenders] = useState<
    Partial<Record<EmailWorkflow, string>>
  >({});
  const [customerReplyTo, setCustomerReplyTo] = useState("");
  const [poRepliesToEmployee, setPoRepliesToEmployee] = useState(true);
  const [employeeReplyEmails, setEmployeeReplyEmails] = useState<
    Record<string, string>
  >({});
  const [officeConfirmationEmail, setOfficeConfirmationEmail] = useState(
    form.email,
  );
  const [signature, setSignature] = useState("");
  const [previewType, setPreviewType] = useState<EmailTemplate["type"]>(
    "estimate_approval_request",
  );
  const [templateDrafts, setTemplateDrafts] = useState(templates);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [templateNotice, setTemplateNotice] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsNotice, setSettingsNotice] = useState("");
  useEffect(() => setTemplateDrafts(templates), [templates]);
  useEffect(() => {
    if (!emailSettings) return;
    const next = emailSettings.settings;
    setAddresses(next.addresses);
    setSenders(next.senders);
    setCustomerReplyTo(next.customerReplyTo ?? "");
    setOfficeConfirmationEmail(next.officeConfirmationEmail ?? "");
    setSignature(next.signature);
    setPoRepliesToEmployee(next.poRepliesToEmployee);
    setEmployeeReplyEmails(next.employeeReplyEmails);
  }, [emailSettings]);
  const previewTemplate = templateDrafts.find(
    (item) => item.type === previewType,
  );
  const previewSender =
    senders[previewType] &&
    emailSettings?.activatedAddresses.includes(senders[previewType]!)
      ? senders[previewType]!
      : "no-reply@workviewpro.com";
  const previewReplyTo =
    customerReplyTo || senders[previewType] || form.email || previewSender;
  const poSenders = team.filter(canSendPurchaseOrders);
  const address = [
    form.addressLine1,
    form.addressLine2,
    [form.city, form.state, form.postalCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const logoAvailable = Boolean(
    company.logoAttachmentId && company.logoAvailability !== "not_copied",
  );

  function addAddress() {
    const email = newAddress.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddressError("Enter a valid email address.");
      return;
    }
    if (addresses.includes(email)) {
      setAddressError("This address is already listed.");
      return;
    }
    setAddresses((current) => [...current, email]);
    setNewAddress("");
    setAddressError("");
  }

  function removeAddress(email: string) {
    setAddresses((current) => current.filter((item) => item !== email));
    setSenders(
      (current) =>
        Object.fromEntries(
          Object.entries(current).filter(([, value]) => value !== email),
        ) as Partial<Record<EmailWorkflow, string>>,
    );
    if (customerReplyTo === email) setCustomerReplyTo("");
  }

  function updatePreviewTemplate(field: "subject" | "body", value: string) {
    setTemplateDrafts((current) =>
      current.map((item) =>
        item.type === previewType ? { ...item, [field]: value } : item,
      ),
    );
    setTemplateNotice("");
  }

  async function savePreviewTemplate() {
    if (!previewTemplate) return;
    setSavingTemplate(true);
    setTemplateError("");
    setTemplateNotice("");
    try {
      await onSaveTemplate(previewTemplate);
      setTemplateNotice("Message template saved.");
    } catch (error) {
      setTemplateError(
        error instanceof Error
          ? error.message
          : "Unable to save message template.",
      );
    } finally {
      setSavingTemplate(false);
    }
  }

  async function saveSettings() {
    setSavingSettings(true);
    setSettingsError("");
    setSettingsNotice("");
    try {
      await onSaveSettings({
        addresses,
        senders: Object.fromEntries(
          Object.entries(senders).filter(([, value]) => Boolean(value)),
        ),
        customerReplyTo: customerReplyTo || null,
        officeConfirmationEmail: officeConfirmationEmail.trim() || null,
        signature,
        poRepliesToEmployee,
        employeeReplyEmails: Object.fromEntries(
          Object.entries(employeeReplyEmails).filter(([, value]) =>
            Boolean(value.trim()),
          ),
        ),
      });
      setSettingsNotice(
        "Email settings saved. Sender addresses remain inactive until verified for delivery.",
      );
    } catch (error) {
      setSettingsError(
        error instanceof Error
          ? error.message
          : "Unable to save email settings.",
      );
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <Panel title="Customize email">
      <div className="mb-5 rounded-lg border border-[var(--line)] bg-[var(--wash)] p-4 text-sm text-[var(--ink-soft)]">
        <strong className="text-[var(--ink)]">Company email settings</strong>
        <p className="mt-1">
          Save addresses and preferences here. Sender addresses require domain
          authorization before they can be used for delivery.
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-4 rounded-lg border border-[var(--line)] p-4">
          <div>
            <h3 className="font-semibold">Company sender addresses</h3>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">
              Add the mailboxes the company may use, such as info@company.com
              and billing@company.com. WorkView must authorize the sending
              domain before an address becomes active.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              aria-label="New company sender address"
              type="email"
              value={newAddress}
              onChange={(event) => {
                setNewAddress(event.target.value);
                setAddressError("");
              }}
              placeholder="info@company.com"
              className="min-w-0 flex-1 rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={addAddress}
              className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold"
            >
              Add address
            </button>
          </div>
          {addressError && (
            <p role="alert" className="text-sm text-[var(--accent)]">
              {addressError}
            </p>
          )}
          <ul className="space-y-2">
            {addresses.map((email) => (
              <li
                key={email}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[var(--wash)] px-3 py-2 text-sm"
              >
                <span>
                  {email}{" "}
                  <span className="text-xs text-[var(--ink-faint)]">
                    {emailSettings?.activatedAddresses.includes(email)
                      ? "Active"
                      : "Pending domain authorization"}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => removeAddress(email)}
                  className="font-semibold text-[var(--accent)]"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          {!addresses.length && (
            <p className="text-sm text-[var(--ink-faint)]">
              Add an address to choose senders below.
            </p>
          )}
        </section>
        <section className="space-y-3 rounded-lg border border-[var(--line)] p-4">
          <h3 className="font-semibold">Which address sends each email?</h3>
          {emailWorkflowLabels.map((workflow) => (
            <label key={workflow.type} className="block text-sm font-medium">
              {workflow.label}
              <select
                aria-label={`${workflow.label} sender`}
                value={senders[workflow.type] || ""}
                onChange={(event) =>
                  setSenders((current) => ({
                    ...current,
                    [workflow.type]: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2"
              >
                <option value="">Choose an address</option>
                {addresses.map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </section>
        <section className="space-y-4 rounded-lg border border-[var(--line)] p-4">
          <h3 className="font-semibold">Replies and confirmations</h3>
          <label className="block text-sm font-medium">
            Customer replies go to
            <select
              aria-label="Customer reply address"
              value={customerReplyTo}
              onChange={(event) => setCustomerReplyTo(event.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2"
            >
              <option value="">The selected sender address</option>
              {addresses.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium">
            Office copy of accepted-estimate confirmation
            <input
              aria-label="Office confirmation email"
              type="email"
              value={officeConfirmationEmail}
              onChange={(event) =>
                setOfficeConfirmationEmail(event.target.value)
              }
              placeholder={form.email || "office@company.com"}
              className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"
            />
          </label>
          <p className="text-xs text-[var(--ink-faint)]">
            If blank, the business email receives the office copy. The customer
            receives a confirmation too.
          </p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={poRepliesToEmployee}
              onChange={(event) => setPoRepliesToEmployee(event.target.checked)}
              className="mt-1"
            />
            <span>
              Send purchase order replies to the employee who sent the order
            </span>
          </label>
        </section>
        <section className="space-y-4 rounded-lg border border-[var(--line)] p-4">
          <h3 className="font-semibold">Employee PO reply addresses</h3>
          <p className="text-sm text-[var(--ink-soft)]">
            Review employees who can send purchase orders. Their saved work
            email is used for vendor replies today.
          </p>
          <div className="max-h-60 space-y-3 overflow-y-auto">
            {poSenders.map((member) => (
              <label key={member.id} className="block text-sm font-medium">
                {member.name}
                <input
                  aria-label={`${member.name} PO reply address`}
                  type="email"
                  value={employeeReplyEmails[member.id] ?? member.email}
                  onChange={(event) =>
                    setEmployeeReplyEmails((current) => ({
                      ...current,
                      [member.id]: event.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"
                />
              </label>
            ))}
          </div>
          {!poSenders.length && (
            <p className="text-sm text-[var(--ink-faint)]">
              No active employees can send purchase orders.
            </p>
          )}
        </section>
        <section className="space-y-3 rounded-lg border border-[var(--line)] p-4 lg:col-span-2">
          <h3 className="font-semibold">Shared email signature</h3>
          <textarea
            aria-label="Shared email signature"
            value={signature}
            onChange={(event) => setSignature(event.target.value)}
            rows={3}
            placeholder={`${form.name}\n${form.phone || "Phone"}\n${form.email || "Email"}`}
            className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
          />
          <p className="text-xs text-[var(--ink-faint)]">
            The company logo and contact details are shown separately in the
            email preview.
          </p>
        </section>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={savingSettings || !emailSettings}
          onClick={() => void saveSettings()}
          className="rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] disabled:opacity-50"
        >
          {savingSettings ? "Saving…" : "Save email settings"}
        </button>
        <span className="text-xs text-[var(--ink-faint)]">
          Addresses and reply choices are saved together.
        </span>
      </div>
      {settingsError && (
        <p role="alert" className="mt-2 text-sm text-[var(--accent)]">
          {settingsError}
        </p>
      )}
      {settingsNotice && (
        <p role="status" className="mt-2 text-sm text-[var(--ink-soft)]">
          {settingsNotice}
        </p>
      )}
      <section className="mt-6 rounded-lg border border-[var(--line)] p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-semibold">Message and preview</h3>
            <p className="mt-1 text-xs text-[var(--ink-faint)]">
              Edit the saved message template here and preview it with the draft
              identity choices above.
            </p>
          </div>
          <select
            aria-label="Preview email type"
            value={previewType}
            onChange={(event) => {
              setPreviewType(event.target.value as EmailTemplate["type"]);
              setTemplateError("");
              setTemplateNotice("");
            }}
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm"
          >
            {emailWorkflowLabels
              .filter((workflow) => workflow.type !== "purchase_order")
              .map((workflow) => (
                <option key={workflow.type} value={workflow.type}>
                  {workflow.label}
                </option>
              ))}
          </select>
        </div>
        {previewTemplate && (
          <div className="mt-4 grid gap-3">
            <label className="block text-sm font-medium">
              Subject
              <input
                aria-label={`${previewType} subject`}
                maxLength={200}
                value={previewTemplate.subject}
                onChange={(event) =>
                  updatePreviewTemplate("subject", event.target.value)
                }
                className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"
              />
            </label>
            <label className="block text-sm font-medium">
              Message
              <textarea
                aria-label={`${previewType} message`}
                maxLength={5000}
                rows={4}
                value={previewTemplate.body}
                onChange={(event) =>
                  updatePreviewTemplate("body", event.target.value)
                }
                className="mt-1 w-full rounded-lg border border-[var(--line)] px-3 py-2"
              />
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={
                  savingTemplate ||
                  !previewTemplate.subject.trim() ||
                  !previewTemplate.body.trim()
                }
                onClick={() => void savePreviewTemplate()}
                className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold disabled:opacity-60"
              >
                {savingTemplate ? "Saving…" : "Save message template"}
              </button>
              <span className="text-xs text-[var(--ink-faint)]">
                The subject and message save separately from the email settings
                above.
              </span>
            </div>
            {templateError && (
              <p role="alert" className="text-sm text-[var(--accent)]">
                {templateError}
              </p>
            )}
            {templateNotice && (
              <p role="status" className="text-sm text-[var(--ink-soft)]">
                {templateNotice}
              </p>
            )}
          </div>
        )}
        <div className="mt-5 overflow-hidden rounded-lg border border-[var(--line)]">
          <div className="space-y-1 bg-[var(--wash)] px-4 py-3 text-xs text-[var(--ink-soft)]">
            <p>
              From: {form.name} &lt;{previewSender}&gt;
            </p>
            <p>Reply-To: {previewReplyTo}</p>
            <p>Subject: {previewTemplate?.subject || "Email subject"}</p>
          </div>
          <div className="space-y-4 p-5">
            {logoAvailable ? (
              <img
                src={`${API_URL}/api/v1/workspace/attachments/${encodeURIComponent(company.logoAttachmentId!)}/content`}
                alt={`${company.name} logo`}
                className="max-h-20 max-w-52 object-contain"
              />
            ) : (
              <strong className="text-lg">{form.name}</strong>
            )}
            <p className="whitespace-pre-wrap text-sm">
              {previewTemplate?.body || "Message text"}
            </p>
            {previewType === "estimate_approval_request" && (
              <span className="inline-block rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)]">
                Review &amp; accept estimate
              </span>
            )}
            {signature && (
              <p className="whitespace-pre-wrap border-t border-[var(--line)] pt-3 text-sm">
                {signature}
              </p>
            )}
            <p className="border-t border-[var(--line)] pt-3 text-xs text-[var(--ink-soft)]">
              {[form.name, address, form.phone, form.email]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </div>
        <p className="mt-2 text-xs text-[var(--ink-faint)]">
          Preview of the saved message and company layout. Delivery uses the
          authorized sender address.
        </p>
      </section>
    </Panel>
  );
}

function EmailDeliveryPanel({ team }: { team: CompanyData["team"] }) {
  const poSenders = team.filter(canSendPurchaseOrders);
  return (
    <Panel title="Current email delivery">
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-[var(--line)] p-4">
          <h3 className="font-semibold">Sender addresses</h3>
          <p className="mt-2 text-sm text-[var(--ink-soft)]">
            A company address becomes the From address after domain
            authorization. Until then, messages use{" "}
            <strong>no-reply@workviewpro.com</strong> with your configured
            Reply-To address.
          </p>
          <p className="mt-3 text-xs text-[var(--ink-faint)]">
            The business email above is a contact detail and can be used for
            replies.
          </p>
        </section>
        <section className="rounded-lg border border-[var(--line)] p-4">
          <h3 className="font-semibold">Replies</h3>
          <p className="mt-2 text-sm text-[var(--ink-soft)]">
            Purchase order replies go to the sending employee when that option
            is enabled. Customer replies use the address selected above.
          </p>
          <p className="mt-3 text-xs text-[var(--ink-faint)]">
            WorkView does not create or host team mailboxes.
          </p>
        </section>
      </div>
      <section className="mt-4 rounded-lg border border-[var(--line)] p-4">
        <h3 className="font-semibold">Estimate approval confirmation</h3>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">
          The customer receives a confirmation. Add an office confirmation
          address above for a separate company copy. Edit the wording in Message
          and preview.
        </p>
      </section>
      <section className="mt-4 rounded-lg border border-[var(--line)] p-4">
        <h3 className="font-semibold">PO sender email addresses</h3>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">
          When an authorized employee sends a purchase order, their saved
          address receives vendor replies.
        </p>
        <ul className="mt-3 divide-y divide-[var(--line)]">
          {poSenders.map((member) => (
            <li
              key={member.id}
              className="flex flex-wrap justify-between gap-2 py-2 text-sm"
            >
              <span className="font-medium">{member.name}</span>
              <span className="text-[var(--ink-soft)]">{member.email}</span>
            </li>
          ))}
        </ul>
        {!poSenders.length && (
          <p className="mt-3 text-sm text-[var(--ink-faint)]">
            No active employees can send purchase orders.
          </p>
        )}
      </section>
    </Panel>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
        {label}
      </span>
      <input
        required={required}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-[13px] text-[var(--ink)] outline-none focus:border-[var(--line)]"
      />
    </label>
  );
}

/**
 * THE WHOLE COMPANY RECORD, EVERY TIME.
 *
 * PATCH /workspace/company rewrites every column it is given, so a partial
 * body blanks the name and the address. Anything that saves one setting has
 * to send all of them — this builds that body from a loaded form, so the
 * pricebook's pricing rules and this screen cannot drift apart.
 */
export function companyPatchBody(form: SettingsForm) {
  const materialMarkupBps = form.materialMarkupPercent.trim()
    ? Math.round(Number(form.materialMarkupPercent) * 100)
    : null;
  const jobNumberDisplayOffset = Number(form.jobNumberDisplayOffset);
  const estimateNumberNext = Number(form.estimateNumberNext);
  const jobTypeMultipliers = form.jobTypeMultipliers.map((jobType) => ({
    id: jobType.id,
    afterHoursServiceMultiplierBps: jobType.multiplier.trim()
      ? Math.round(Number(jobType.multiplier) * 10_000)
      : null,
  }));
  if (
    !Number.isFinite(materialMarkupBps ?? 0) ||
    !Number.isInteger(jobNumberDisplayOffset) ||
    jobNumberDisplayOffset < 0 ||
    jobNumberDisplayOffset > 99_999 ||
    !form.estimateNumberPrefix.trim() ||
    form.estimateNumberPrefix.trim().length > 20 ||
    !/^[A-Za-z0-9-]+$/.test(form.estimateNumberPrefix.trim()) ||
    !Number.isInteger(estimateNumberNext) ||
    estimateNumberNext < 1 ||
    estimateNumberNext > 999_999_999 ||
    jobTypeMultipliers.some(
      (jobType) =>
        jobType.afterHoursServiceMultiplierBps !== null &&
        !Number.isFinite(jobType.afterHoursServiceMultiplierBps),
    )
  )
    throw new Error("Enter valid pricing values.");
  const {
    materialMarkupPercent: _materialMarkupPercent,
    jobNumberDisplayOffset: _jobNumberDisplayOffset,
    estimateNumberPrefix: _estimateNumberPrefix,
    estimateNumberNext: _estimateNumberNext,
    ...companyFields
  } = form;
  return {
    ...companyFields,
    serviceAreas: form.serviceAreas.map(
      ({ citiesText, postalCodesText, ...area }) => ({
        ...area,
        state: area.state || null,
        cities: citiesText
          .split(/[,\n]/)
          .map((value) => value.trim())
          .filter(Boolean),
        postalCodes: postalCodesText
          .split(/[,\n]/)
          .map((value) => value.trim())
          .filter(Boolean),
      }),
    ),
    jobNumberDisplayOffset,
    estimateNumberPrefix: form.estimateNumberPrefix.trim(),
    estimateNumberNext,
    materialMarkupBps,
    jobTypeMultipliers,
    lunchStartsAt: form.lunchStartsAt || null,
    lunchEndsAt: form.lunchEndsAt || null,
    legalName: form.legalName || null,
    phone: form.phone || null,
    country: form.country || null,
    taxNumber: form.taxNumber || null,
    addressLine1: form.addressLine1 || null,
    addressLine2: form.addressLine2 || null,
    city: form.city || null,
    state: form.state || null,
    postalCode: form.postalCode || null,
  };
}

export default function MyCompanyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = authClient.useSession();
  const [data, setData] = useState<CompanyData | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [selectedPanel, setSelectedPanel] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("settings") === "zones") return "Zones";
    return params.has("quickbooks")
      ? "QuickBooks Online"
      : params.has("stripe")
        ? "Stripe payments"
        : null;
  });
  const [activeTab, setActiveTab] = useState<CompanyTab>(() =>
    new URLSearchParams(location.search).get("settings") === "zones"
      ? "zones"
      : "overview",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [editingMember, setEditingMember] = useState<
    CompanyData["team"][number] | null
  >(null);
  const [editingRole, setEditingRole] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState("");
  const [skillName, setSkillName] = useState("");
  const [skillError, setSkillError] = useState("");
  const [jobTypeName, setJobTypeName] = useState("");
  const [jobTypeError, setJobTypeError] = useState("");
  const [editingJobTypeId, setEditingJobTypeId] = useState<string | null>(null);
  const [editingJobTypeName, setEditingJobTypeName] = useState("");
  const [editingJobTypeSkillIds, setEditingJobTypeSkillIds] = useState<
    string[]
  >([]);
  const [pricingReadiness, setPricingReadiness] =
    useState<PricingReadiness | null>(null);
  const [jobSkillDefaultsPreview, setJobSkillDefaultsPreview] =
    useState<JobSkillDefaultsPreview | null>(null);
  const [jobSkillDefaultsPreviewError, setJobSkillDefaultsPreviewError] =
    useState("");
  const [profileMember, setProfileMember] = useState<
    CompanyData["team"][number] | null
  >(null);
  const [technicianProfile, setTechnicianProfile] = useState<{
    name: string;
    email: string;
    phone: string;
    active: boolean;
    dispatchable: boolean;
    skillIds: string[];
    costRateCents?: number | null;
    showCostRate: boolean;
  } | null>(null);
  const [tagName, setTagName] = useState("");
  const [tagError, setTagError] = useState("");
  const [profileError, setProfileError] = useState("");

  async function saveLaborBilling(
    jobType: CompanyData["jobTypes"][number],
    enabled: boolean,
    priceBookItemId: string | null,
    minimumMinutes: number,
    roundingIncrementMinutes: number,
  ) {
    try {
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/job-types/${encodeURIComponent(jobType.id)}/labor-billing`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            enabled,
            priceBookItemId,
            minimumMinutes,
            roundingIncrementMinutes,
          }),
        },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to save labor billing rule",
        );
      await loadCompany();
    } catch (cause) {
      setJobTypeError(
        cause instanceof Error
          ? cause.message
          : "Unable to save labor billing rule",
      );
    }
  }

  useEffect(() => {
    if (!isPending && !session?.user) navigate("/auth", { replace: true });
  }, [isPending, navigate, session?.user]);

  async function loadCompany() {
    setError("");
    const response = await apiFetch("/api/v1/workspace/company");
    if (!response.ok) throw new Error("Unable to load company settings");
    const result = (await response.json()) as { data: CompanyData };
    setData(result.data);
    setForm(formFromData(result.data));
    // The readiness report scans operational data and may take longer than the
    // settings payload, especially after a large import. It is informative,
    // so it must never block the editable company settings from rendering.
    void apiFetch("/api/v1/workspace/pricing-readiness")
      .then(async (readinessResponse) => {
        if (!readinessResponse.ok) return setPricingReadiness(null);
        const readiness = (await readinessResponse.json()) as {
          data: PricingReadiness;
        };
        setPricingReadiness(readiness.data);
      })
      .catch(() => setPricingReadiness(null));
  }

  useEffect(() => {
    if (!session?.user) return;
    void loadCompany()
      .catch(() =>
        setError(
          "We couldn’t load company information. Please refresh and try again.",
        ),
      )
      .finally(() => setLoading(false));
  }, [session?.user]);

  // The customer count was a tile on the deleted Home screen. It is company
  // information, so it lives here, and it is a door into the list. QuickBooks
  // lived on that same unused Home/Dashboard pair; company settings is the
  // remaining owner surface for integrations.
  const [customerCount, setCustomerCount] = useState<number | null>(null);
  const [quickBooks, setQuickBooks] = useState<QuickBooksStatus | null>(null);
  const [quickBooksUnavailable, setQuickBooksUnavailable] = useState(false);
  const [quickBooksMessage, setQuickBooksMessage] = useState("");
  const [quickBooksError, setQuickBooksError] = useState("");
  const [isDisconnectingQuickBooks, setIsDisconnectingQuickBooks] =
    useState(false);
  const [quickBooksSetup, setQuickBooksSetup] =
    useState<QuickBooksAccountingSetup | null>(null);
  const [isScanningQuickBooks, setIsScanningQuickBooks] = useState(false);
  const [isSavingQuickBooksSetup, setIsSavingQuickBooksSetup] = useState(false);
  const [isSavingQuickBooksBillMapping, setIsSavingQuickBooksBillMapping] =
    useState(false);
  const [stripeAccount, setStripeAccount] =
    useState<StripeAccountStatus | null>(null);
  const [stripeTaxSettings, setStripeTaxSettings] =
    useState<StripeTaxSettings | null>(null);
  const [twilioConnection, setTwilioConnection] =
    useState<TwilioConnectionStatus | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailSettings, setEmailSettings] =
    useState<EmailSettingsResponse | null>(null);
  const [emailSettingsUnavailable, setEmailSettingsUnavailable] =
    useState(false);
  const [stripeUnavailable, setStripeUnavailable] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [isStartingStripe, setIsStartingStripe] = useState(false);
  const [isOpeningStripeDashboard, setIsOpeningStripeDashboard] =
    useState(false);
  const [isDisconnectingStripe, setIsDisconnectingStripe] = useState(false);
  useEffect(() => {
    if (!session?.user) return;
    let live = true;
    apiFetch("/api/v1/workspace/overview")
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (live && payload?.data?.counts)
          setCustomerCount(payload.data.counts.customers ?? null);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [session?.user]);
  useEffect(() => {
    if (!session?.user) return;
    void apiFetch("/api/v1/workspace/company/payment-settings/stripe-tax")
      .then(async (response) =>
        response.ok
          ? ((await response.json()) as {
              data: { settings: StripeTaxSettings };
            })
          : null,
      )
      .then((payload) => {
        if (payload) setStripeTaxSettings(payload.data.settings);
      })
      .catch(() => {});
  }, [session?.user]);
  useEffect(() => {
    if (!session?.user) return;
    void apiFetch("/api/v1/workspace/company/email-templates")
      .then(async (response) =>
        response.ok
          ? ((await response.json()) as {
              data: { templates: EmailTemplate[] };
            })
          : null,
      )
      .then((payload) => {
        if (payload) setEmailTemplates(payload.data.templates);
      })
      .catch(() => {});
  }, [session?.user]);
  useEffect(() => {
    if (!session?.user) return;
    void apiFetch("/api/v1/workspace/company/email-settings")
      .then(async (response) => {
        if (!response.ok) {
          setEmailSettingsUnavailable(true);
          return null;
        }
        return (await response.json()) as { data: EmailSettingsResponse };
      })
      .then((payload) => {
        if (payload) {
          setEmailSettings(payload.data);
          setEmailSettingsUnavailable(false);
        }
      })
      .catch(() => setEmailSettingsUnavailable(true));
  }, [session?.user]);
  useEffect(() => {
    if (!session?.user) return;
    void apiFetch("/api/v1/workspace/company/integrations/twilio")
      .then(async (response) =>
        response.ok
          ? ((await response.json()) as { data: TwilioConnectionStatus })
          : null,
      )
      .then((payload) => {
        if (payload) setTwilioConnection(payload.data);
      })
      .catch(() => {});
  }, [session?.user]);
  useEffect(() => {
    if (!session?.user || !quickBooks?.connected) {
      setQuickBooksSetup(null);
      return;
    }
    void apiFetch("/api/v1/integrations/quickbooks/setup")
      .then(async (response) =>
        response.ok
          ? ((await response.json()) as { data: QuickBooksAccountingSetup })
          : null,
      )
      .then((payload) => {
        if (payload?.data)
          setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      })
      .catch(() => {});
  }, [session?.user, quickBooks?.connected]);
  useEffect(() => {
    if (!session?.user) return;
    let live = true;
    void apiFetch("/api/v1/workspace/company/payment-settings/stripe")
      .then(async (response) => {
        if (response.status === 503) {
          if (live) setStripeUnavailable(true);
          return null;
        }
        if (!response.ok) return null;
        return (await response.json()) as {
          data: { account: StripeAccountStatus | null };
        };
      })
      .then((payload) => {
        if (!live || !payload) return;
        setStripeAccount(payload.data.account);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [session?.user]);
  useEffect(() => {
    if (
      !session?.user ||
      new URLSearchParams(window.location.search).get("stripe") !== "refresh"
    )
      return;
    let live = true;
    setStripeError("");
    setIsStartingStripe(true);
    // Stripe can only redirect back to a fixed refresh URL. Remove its marker
    // before requesting a replacement link so a reload cannot create another.
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${window.location.hash}`,
    );
    void apiFetch(
      "/api/v1/workspace/company/payment-settings/stripe/onboarding/refresh",
      { method: "POST" },
    )
      .then(async (response) => {
        const payload = (await response.json()) as {
          data?: { onboardingUrl?: string };
          error?: { message?: string };
        };
        if (!response.ok || !payload.data?.onboardingUrl)
          throw new Error(
            payload.error?.message || "Unable to refresh Stripe setup.",
          );
        if (live) window.location.assign(payload.data.onboardingUrl);
      })
      .catch((cause) => {
        if (live)
          setStripeError(
            cause instanceof Error
              ? cause.message
              : "Unable to refresh Stripe setup.",
          );
      })
      .finally(() => {
        if (live) setIsStartingStripe(false);
      });
    return () => {
      live = false;
    };
  }, [session?.user]);
  useEffect(() => {
    const result = parseQuickBooksCallback(
      new URLSearchParams(window.location.search).get("quickbooks"),
    );
    if (!result) return;
    setQuickBooksMessage(quickBooksCallbackMessage(result));
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${window.location.hash}`,
    );
  }, []);
  useEffect(() => {
    if (!session?.user) return;
    let live = true;
    void apiFetch("/api/v1/integrations/quickbooks")
      .then(async (response) => {
        if (response.status === 503) {
          if (live) setQuickBooksUnavailable(true);
          return null;
        }
        if (!response.ok) return null;
        return (await response.json()) as { data: QuickBooksStatus };
      })
      .then((payload) => {
        if (!live || !payload) return;
        setQuickBooks(payload.data);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [session?.user]);
  function connectQuickBooks() {
    setQuickBooksMessage("");
    setQuickBooksError("");
    window.location.assign(`${API_URL}/api/v1/integrations/quickbooks/connect`);
  }
  async function disconnectQuickBooks() {
    setQuickBooksError("");
    setQuickBooksMessage("");
    setIsDisconnectingQuickBooks(true);
    try {
      const response = await apiFetch("/api/v1/integrations/quickbooks", {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Unable to disconnect QuickBooks");
      setQuickBooks((current) =>
        current
          ? {
              ...current,
              connected: false,
              realmId: null,
              environment: null,
              connectedAt: null,
            }
          : current,
      );
      setQuickBooksMessage("QuickBooks Online was disconnected.");
    } catch {
      setQuickBooksError(
        "We couldn’t disconnect QuickBooks. Please try again.",
      );
    } finally {
      setIsDisconnectingQuickBooks(false);
    }
  }
  async function confirmQuickBooksRealmSwitch() {
    setQuickBooksError("");
    setQuickBooksMessage("");
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/realm-switch/confirm",
        { method: "POST" },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksStatus;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(
          payload.error?.message || "Unable to switch QuickBooks companies",
        );
      setQuickBooks(payload.data);
      setQuickBooksSetup(null);
      setQuickBooksMessage(
        "QuickBooks company switched. Run a setup scan and save new accounting mappings before syncing.",
      );
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "We couldn’t switch QuickBooks companies.",
      );
    }
  }
  async function cancelQuickBooksRealmSwitch() {
    setQuickBooksError("");
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/realm-switch",
        { method: "DELETE" },
      );
      if (!response.ok)
        throw new Error("Unable to cancel QuickBooks company switch");
      setQuickBooks((current) =>
        current ? { ...current, pendingRealmId: null } : current,
      );
      setQuickBooksMessage("Kept the current QuickBooks company.");
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "We couldn’t cancel the QuickBooks company switch.",
      );
    }
  }
  async function scanQuickBooksSetup() {
    setQuickBooksError("");
    setIsScanningQuickBooks(true);
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/scan",
        { method: "POST" },
      );
      if (!response.ok) throw new Error("Unable to scan QuickBooks");
      const setupResponse = await apiFetch(
        "/api/v1/integrations/quickbooks/setup",
      );
      if (!setupResponse.ok) throw new Error("Unable to load QuickBooks setup");
      const payload = (await setupResponse.json()) as {
        data?: QuickBooksAccountingSetup;
      };
      if (!payload.data) throw new Error("Unable to load QuickBooks setup");
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage("QuickBooks accounting setup was scanned.");
    } catch {
      setQuickBooksError(
        "We couldn’t scan QuickBooks. Please reconnect and try again.",
      );
    } finally {
      setIsScanningQuickBooks(false);
    }
  }
  async function saveQuickBooksSetup(
    mappings: Array<{ category: string; quickBooksAccountId: string }>,
  ) {
    setQuickBooksError("");
    setIsSavingQuickBooksSetup(true);
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/mappings",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ mappings }),
        },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksAccountingSetup;
      };
      if (!response.ok || !payload.data)
        throw new Error("Unable to save QuickBooks mappings");
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage(
        payload.data.queuedManualPaymentCount
          ? `QuickBooks mappings were saved. ${payload.data.queuedManualPaymentCount} existing manual payment${payload.data.queuedManualPaymentCount === 1 ? " was" : "s were"} queued.`
          : "QuickBooks mappings were saved.",
      );
    } catch {
      setQuickBooksError(
        "We couldn’t save the accounting mappings. Check each selected account and try again.",
      );
    } finally {
      setIsSavingQuickBooksSetup(false);
    }
  }
  async function saveQuickBooksSourceAccountMappings(
    mappings: Array<{
      accountKind: "income" | "expense";
      sourceAccountName: string;
      quickBooksAccountId: string;
    }>,
  ) {
    setQuickBooksError("");
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/pricebook-accounts",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ mappings }),
        },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksAccountingSetup;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(
          payload.error?.message || "Unable to save Pricebook account mappings",
        );
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage("Pricebook account mappings were saved.");
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "Unable to save Pricebook account mappings.",
      );
    }
  }
  async function createQuickBooksAccountingAccount(
    accountKind: "income" | "expense",
    name: string,
  ) {
    setQuickBooksError("");
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/accounting-accounts",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ accountKind, name }),
        },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksAccountingSetup;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(
          payload.error?.message || "Unable to add accounting account",
        );
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage(
        "Accounting account added. Map it to QuickBooks before using it on an invoice or bill.",
      );
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "Unable to add accounting account.",
      );
    }
  }
  async function saveQuickBooksCustomerDiscountAccount(accountId: string) {
    setQuickBooksError("");
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/customer-discount-account",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ accountId }),
        },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksAccountingSetup;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(
          payload.error?.message || "Unable to save customer discount account",
        );
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage("Customer discount account saved.");
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "We couldn’t save the customer discount account.",
      );
    }
  }
  async function saveQuickBooksBillMapping(
    apAccountId: string,
    expenseAccountId: string,
  ) {
    setQuickBooksError("");
    setIsSavingQuickBooksBillMapping(true);
    try {
      const response = await apiFetch(
        "/api/v1/integrations/quickbooks/setup/bill-mapping",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ apAccountId, expenseAccountId }),
        },
      );
      const payload = (await response.json()) as {
        data?: QuickBooksAccountingSetup;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(
          payload.error?.message || "Unable to save bill accounts",
        );
      setQuickBooksSetup(normalizeQuickBooksSetup(payload.data));
      setQuickBooksMessage("QuickBooks bill accounts were saved.");
    } catch (cause) {
      setQuickBooksError(
        cause instanceof Error
          ? cause.message
          : "We couldn’t save the bill accounts.",
      );
    } finally {
      setIsSavingQuickBooksBillMapping(false);
    }
  }
  async function startStripeOnboarding() {
    setStripeError("");
    setIsStartingStripe(true);
    try {
      const response = await apiFetch(
        "/api/v1/workspace/company/payment-settings/stripe/onboarding",
        { method: "POST" },
      );
      const payload = (await response.json()) as {
        data?: { onboardingUrl?: string };
        error?: { message?: string };
      };
      if (!response.ok || !payload.data?.onboardingUrl)
        throw new Error(
          payload.error?.message || "Unable to open Stripe setup.",
        );
      window.location.assign(payload.data.onboardingUrl);
    } catch (cause) {
      setStripeError(
        cause instanceof Error ? cause.message : "Unable to open Stripe setup.",
      );
      setIsStartingStripe(false);
    }
  }
  async function adoptStripeAccount(accountId: string) {
    setStripeError("");
    setIsStartingStripe(true);
    try {
      const response = await apiFetch(
        "/api/v1/workspace/company/payment-settings/stripe/adopt",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ accountId: accountId.trim() }),
        },
      );
      const payload = (await response.json()) as {
        data?: { account?: StripeAccountStatus };
        error?: { message?: string };
      };
      if (!response.ok || !payload.data?.account)
        throw new Error(
          payload.error?.message || "Unable to link the Stripe account.",
        );
      setStripeAccount(payload.data.account);
    } catch (cause) {
      setStripeError(
        cause instanceof Error
          ? cause.message
          : "Unable to link the Stripe account.",
      );
    } finally {
      setIsStartingStripe(false);
    }
  }
  async function openStripeDashboard() {
    setStripeError("");
    setIsOpeningStripeDashboard(true);
    try {
      const response = await apiFetch(
        "/api/v1/workspace/company/payment-settings/stripe/dashboard-link",
        { method: "POST" },
      );
      const payload = (await response.json()) as {
        data?: { dashboardUrl?: string };
        error?: { message?: string };
      };
      if (!response.ok || !payload.data?.dashboardUrl)
        throw new Error(
          payload.error?.message ||
            "Unable to open the Stripe Express dashboard.",
        );
      window.location.assign(payload.data.dashboardUrl);
    } catch (cause) {
      setStripeError(
        cause instanceof Error
          ? cause.message
          : "Unable to open the Stripe Express dashboard.",
      );
      setIsOpeningStripeDashboard(false);
    }
  }
  async function disconnectStripe() {
    if (
      !stripeAccount ||
      !window.confirm(
        "Disconnect Stripe? Open payment links will be cancelled and card payments will stop immediately. You can connect a new Stripe account afterward.",
      )
    )
      return;
    setStripeError("");
    setIsDisconnectingStripe(true);
    try {
      const response = await apiFetch(
        "/api/v1/workspace/company/payment-settings/stripe",
        { method: "DELETE" },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to disconnect Stripe.",
        );
      setStripeAccount(null);
    } catch (cause) {
      setStripeError(
        cause instanceof Error ? cause.message : "Unable to disconnect Stripe.",
      );
    } finally {
      setIsDisconnectingStripe(false);
    }
  }
  async function saveStripeTaxSettings(settings: StripeTaxSettings) {
    const response = await apiFetch(
      "/api/v1/workspace/company/payment-settings/stripe-tax",
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(settings),
      },
    );
    const payload = (await response.json()) as {
      data?: { settings?: StripeTaxSettings };
      error?: { message?: string };
    };
    if (!response.ok || !payload.data?.settings)
      throw new Error(
        payload.error?.message || "Unable to save Stripe Tax settings.",
      );
    setStripeTaxSettings(payload.data.settings);
  }
  async function saveEmailTemplate(template: EmailTemplate) {
    const response = await apiFetch(
      "/api/v1/workspace/company/email-templates",
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          type: template.type,
          subject: template.subject.trim(),
          body: template.body.trim(),
        }),
      },
    );
    const payload = (await response.json()) as {
      data?: { template?: EmailTemplate };
      error?: { message?: string };
    };
    if (!response.ok || !payload.data?.template)
      throw new Error(
        payload.error?.message || "Unable to save email template.",
      );
    setEmailTemplates((current) =>
      current.map((item) =>
        item.type === payload.data!.template!.type
          ? payload.data!.template!
          : item,
      ),
    );
  }
  async function saveEmailSettings(settings: EmailSettings) {
    const response = await apiFetch(
      "/api/v1/workspace/company/email-settings",
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(settings),
      },
    );
    const payload = (await response.json()) as {
      data?: EmailSettingsResponse;
      error?: { message?: string };
    };
    if (!response.ok || !payload.data)
      throw new Error(
        payload.error?.message || "Unable to save email settings.",
      );
    setEmailSettings(payload.data);
  }

  const [showInactive, setShowInactive] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [teamMessage, setTeamMessage] = useState("");
  const [memberUpdating, setMemberUpdating] = useState<string | null>(null);
  const memberUpdatingRef = useRef(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"Admin" | "Dispatcher">(
    "Dispatcher",
  );
  const [technicianName, setTechnicianName] = useState("");
  const [technicianEmail, setTechnicianEmail] = useState("");
  const [technicianPhone, setTechnicianPhone] = useState("");
  const visibleTeam = useMemo(
    () => (data?.team ?? []).filter((member) => showInactive || member.active),
    [data, showInactive],
  );
  const inactiveCount = useMemo(
    () => (data?.team ?? []).filter((member) => !member.active).length,
    [data],
  );
  async function setMemberActive(
    member: CompanyData["team"][number],
    active: boolean,
  ) {
    if (memberUpdatingRef.current) return;
    memberUpdatingRef.current = true;
    setMemberUpdating(member.id);
    setTeamError("");
    setTeamMessage("");
    try {
      await updateOfficeUserActive(member.id, active);
      setData((current) =>
        current
          ? {
              ...current,
              team: current.team.map((row) =>
                row.id === member.id
                  ? {
                      ...row,
                      active,
                      ...(!active
                        ? { technicianActive: false, dispatchable: false }
                        : {}),
                    }
                  : row,
              ),
            }
          : current,
      );
      setTeamMessage(
        `${member.name} ${active ? "reactivated" : "deactivated"}.`,
      );
    } catch (error) {
      setTeamError(
        error instanceof Error
          ? error.message
          : `We couldn’t update ${member.name}.`,
      );
    } finally {
      memberUpdatingRef.current = false;
      setMemberUpdating(null);
    }
  }
  async function removeMember(member: CompanyData["team"][number]) {
    if (
      !window.confirm(
        `Delete ${member.name}? This is only available when they have no work history.`,
      )
    )
      return;
    setTeamError("");
    setTeamMessage("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/team/${encodeURIComponent(member.id)}`,
        { method: "DELETE" },
      );
      const payload = response.status === 204 ? null : await response.json();
      if (!response.ok)
        throw new Error(
          payload?.error?.message || "We couldn’t delete that team member.",
        );
      await loadCompany();
      setTeamMessage(`${member.name} was deleted.`);
    } catch (error) {
      setTeamError(
        error instanceof Error
          ? error.message
          : "We couldn’t delete that team member.",
      );
    }
  }
  async function inviteUser() {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setTeamError("");
    setTeamMessage("");
    try {
      await inviteOfficeUser({
        name: inviteName.trim(),
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteName("");
      setInviteEmail("");
      setInviteRole("Dispatcher");
      await loadCompany();
      setTeamMessage("Invitation sent.");
    } catch (error) {
      setTeamError(
        error instanceof Error
          ? error.message
          : "We couldn’t send that invitation.",
      );
    }
  }
  async function addTechnician() {
    if (!technicianName.trim() || !technicianEmail.trim()) return;
    setTeamError("");
    setTeamMessage("");
    try {
      const response = await apiFetch(
        "/api/v1/workspace/dispatch/technicians",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            name: technicianName.trim(),
            email: technicianEmail.trim(),
            phone: technicianPhone.trim() || null,
          }),
        },
      );
      if (!response.ok) throw new Error("That didn’t save.");
      setTechnicianName("");
      setTechnicianEmail("");
      setTechnicianPhone("");
      await loadCompany();
    } catch {
      setTeamError("We couldn’t add that technician.");
    }
  }
  async function resendSetup(member: CompanyData["team"][number]) {
    setTeamError("");
    setTeamMessage("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/technicians/${encodeURIComponent(member.id)}/password-setup`,
        { method: "POST" },
      );
      const payload = (await response.json()) as {
        data?: { loginCreated?: boolean };
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "That didn’t send.");
      setTeamMessage(
        payload.data?.loginCreated
          ? `Login created and password setup email sent to ${member.email}.`
          : `Password setup email sent to ${member.email}.`,
      );
    } catch (error) {
      setTeamError(
        error instanceof Error
          ? error.message
          : `We couldn’t send setup to ${member.name}.`,
      );
    }
  }
  const businessTypes = useMemo(() => {
    if (!data?.profile?.businessTypes) return "";
    try {
      const parsed = JSON.parse(data.profile.businessTypes);
      const rendered = Array.isArray(parsed)
        ? parsed.join(", ")
        : String(parsed);
      return rendered || "Not configured";
    } catch {
      return data.profile.businessTypes;
    }
  }, [data?.profile?.businessTypes]);

  function update<K extends keyof SettingsForm>(
    key: K,
    value: SettingsForm[K],
  ) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setSaveMessage("");
  }
  function updateHour(
    dayOfWeek: number,
    patch: Partial<SettingsForm["businessHours"][number]>,
  ) {
    setForm((current) =>
      current
        ? {
            ...current,
            businessHours: current.businessHours.map((hours) =>
              hours.dayOfWeek === dayOfWeek ? { ...hours, ...patch } : hours,
            ),
          }
        : current,
    );
    setSaveMessage("");
  }
  function updateServiceArea(
    index: number,
    patch: Partial<SettingsForm["serviceAreas"][number]>,
  ) {
    setForm((current) =>
      current
        ? {
            ...current,
            serviceAreas: current.serviceAreas.map((area, currentIndex) =>
              currentIndex === index ? { ...area, ...patch } : area,
            ),
          }
        : current,
    );
    setSaveMessage("");
  }
  function addServiceArea() {
    setForm((current) =>
      current
        ? {
            ...current,
            serviceAreas: [
              ...current.serviceAreas,
              {
                name: "",
                active: true,
                color: [
                  "#2563eb",
                  "#0d9488",
                  "#9333ea",
                  "#d97706",
                  "#e11d48",
                  "#0891b2",
                ][current.serviceAreas.length % 6],
                citiesText: "",
                state: "",
                postalCodesText: "",
              },
            ],
          }
        : current,
    );
    setSaveMessage("");
  }

  async function uploadCompanyLogo(file: File | undefined) {
    if (!file) return;
    setError("");
    try {
      const body = new FormData();
      body.set("photo", file);
      const response = await apiFetch("/api/v1/workspace/company/logo", {
        method: "POST",
        body,
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to upload company logo",
        );
      await loadCompany();
      setSaveMessage("Company logo updated.");
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to upload company logo.",
      );
    }
  }
  async function removeCompanyLogo() {
    setError("");
    try {
      const response = await apiFetch("/api/v1/workspace/company/logo", {
        method: "DELETE",
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to remove company logo",
        );
      await loadCompany();
      setSaveMessage("Company logo removed.");
    } catch (removeError) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Unable to remove company logo.",
      );
    }
  }
  async function uploadTechnicianPhoto(file: File | undefined) {
    if (!file || !profileMember) return;
    setProfileError("");
    try {
      const body = new FormData();
      body.set("photo", file);
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/technicians/${profileMember.id}/photo`,
        { method: "POST", body },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to upload technician photo",
        );
      await loadCompany();
    } catch (uploadError) {
      setProfileError(
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to upload technician photo.",
      );
    }
  }
  async function removeTechnicianPhoto() {
    if (!profileMember) return;
    setProfileError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/technicians/${profileMember.id}/photo`,
        { method: "DELETE" },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to remove technician photo",
        );
      await loadCompany();
    } catch (removeError) {
      setProfileError(
        removeError instanceof Error
          ? removeError.message
          : "Unable to remove technician photo.",
      );
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const response = await apiFetch("/api/v1/workspace/company", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(companyPatchBody(form)),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to save company settings",
        );
      await loadCompany();
      setSaveMessage("Company settings saved.");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to save company settings.",
      );
    } finally {
      setSaving(false);
    }
  }

  function openRoleEditor(member: CompanyData["team"][number]) {
    if (member.isOwner) return;
    setEditingMember(member);
    // Imported tenants can still have the legacy "Member" role. The role
    // migration maps those members to Admin, so use the same safe default in
    // the editor rather than leaving the select value without a matching option.
    setEditingRole(
      data?.roles.some((role) => role.name === member.role)
        ? member.role
        : "Admin",
    );
    setRoleError("");
  }

  async function saveRole() {
    if (!editingMember) return;
    setRoleSaving(true);
    setRoleError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/team/${encodeURIComponent(editingMember.id)}/role`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ role: editingRole }),
        },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to update member role",
        );
      await loadCompany();
      setEditingMember(null);
    } catch (roleSaveError) {
      setRoleError(
        roleSaveError instanceof Error
          ? roleSaveError.message
          : "Unable to update member role",
      );
    } finally {
      setRoleSaving(false);
    }
  }

  async function createSkill() {
    if (!skillName.trim()) return;
    setSkillError("");
    try {
      const response = await apiFetch("/api/v1/workspace/skills", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: skillName }),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to create skill");
      setSkillName("");
      await loadCompany();
    } catch (error) {
      setSkillError(
        error instanceof Error ? error.message : "Unable to create skill",
      );
    }
  }
  async function createJobType() {
    if (!jobTypeName.trim()) return;
    setJobTypeError("");
    try {
      const response = await apiFetch("/api/v1/workspace/job-types", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: jobTypeName,
          afterHoursServiceMultiplier: null,
        }),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to create job type");
      setJobTypeName("");
      await loadCompany();
    } catch (error) {
      setJobTypeError(
        error instanceof Error ? error.message : "Unable to create job type",
      );
    }
  }
  async function updateJobType(id: string, patch: Record<string, unknown>) {
    setJobTypeError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/job-types/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to save job type");
      await loadCompany();
    } catch (error) {
      setJobTypeError(
        error instanceof Error ? error.message : "Unable to save job type",
      );
    }
  }
  async function updateSkill(id: string, patch: Record<string, unknown>) {
    setSkillError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/skills/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to save skill");
      await loadCompany();
    } catch (error) {
      setSkillError(
        error instanceof Error ? error.message : "Unable to save skill",
      );
    }
  }
  async function createTag() {
    if (!tagName.trim()) return;
    setTagError("");
    try {
      const response = await apiFetch("/api/v1/workspace/tags", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: tagName.trim() }),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to create tag");
      setTagName("");
      await loadCompany();
    } catch (error) {
      setTagError(
        error instanceof Error ? error.message : "Unable to create tag",
      );
    }
  }
  async function updateTag(id: string, patch: Record<string, unknown>) {
    setTagError("");
    try {
      const response = await apiFetch(`/api/v1/workspace/tags/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(payload.error?.message || "Unable to save tag");
      await loadCompany();
    } catch (error) {
      setTagError(
        error instanceof Error ? error.message : "Unable to save tag",
      );
    }
  }
  async function reviewJobSkillDefaults() {
    setJobSkillDefaultsPreviewError("");
    try {
      const response = await apiFetch(
        "/api/v1/workspace/job-skill-defaults/preview",
      );
      const payload = (await response.json()) as {
        data?: JobSkillDefaultsPreview;
        error?: { message?: string };
      };
      if (!response.ok || !payload.data)
        throw new Error(payload.error?.message || "Unable to review open jobs");
      setJobSkillDefaultsPreview(payload.data);
    } catch (error) {
      setJobSkillDefaultsPreviewError(
        error instanceof Error ? error.message : "Unable to review open jobs",
      );
    }
  }
  async function openProfile(member: CompanyData["team"][number]) {
    setProfileMember(member);
    setProfileError("");
    try {
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/technicians/${member.id}/profile`,
      );
      const result = (await response.json()) as {
        data?: {
          item: {
            name: string;
            email: string;
            phone: string | null;
            active: boolean;
            dispatchable: boolean;
            skillIds: string[];
            costRateCents?: number | null;
          };
        };
        error?: { message?: string };
      };
      if (!response.ok || !result.data)
        throw new Error(
          result.error?.message || "Unable to load technician profile",
        );
      const {
        name,
        email,
        phone,
        active,
        dispatchable,
        skillIds,
        costRateCents,
      } = result.data.item;
      setTechnicianProfile({
        name,
        email,
        phone: phone || "",
        active,
        dispatchable,
        skillIds,
        costRateCents: costRateCents ?? null,
        showCostRate: Object.prototype.hasOwnProperty.call(
          result.data.item,
          "costRateCents",
        ),
      });
    } catch (error) {
      setProfileError(
        error instanceof Error
          ? error.message
          : "Unable to load technician profile",
      );
    }
  }
  async function saveProfile() {
    if (!profileMember || !technicianProfile) return;
    setProfileError("");
    try {
      const body: Record<string, unknown> = {
        name: technicianProfile.name,
        email: technicianProfile.email,
        phone: technicianProfile.phone || null,
        active: technicianProfile.active,
        dispatchable: technicianProfile.dispatchable,
        skillIds: technicianProfile.skillIds,
      };
      if (technicianProfile.showCostRate)
        body.costRateCents = technicianProfile.costRateCents ?? null;
      const response = await apiFetch(
        `/api/v1/workspace/dispatch/technicians/${profileMember.id}/profile`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      const payload = (await response.json()) as {
        error?: { message?: string };
      };
      if (!response.ok)
        throw new Error(
          payload.error?.message || "Unable to save technician profile",
        );
      await loadCompany();
      setProfileMember(null);
      setTechnicianProfile(null);
    } catch (error) {
      setProfileError(
        error instanceof Error
          ? error.message
          : "Unable to save technician profile",
      );
    }
  }

  if (isPending || !session?.user || loading || !form || !data?.company)
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--wash)] text-sm text-[var(--ink-soft)]">
        Loading company settings…
      </main>
    );
  if (error && !data)
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--wash)] px-6 text-center">
        <div>
          <p className="text-sm text-[var(--accent)]">{error}</p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)]"
          >
            Back to dashboard
          </Link>
        </div>
      </main>
    );

  const { company, profile, serviceAreas, team } = data;
  const activeAreas = serviceAreas.filter((area) => area.active);
  return (
    <main className="wv min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <header className="sticky top-0 z-20 flex min-h-[74px] flex-wrap items-center justify-between gap-3 py-3 border-b border-[var(--line)] bg-[var(--paper)]/95 px-5 backdrop-blur sm:px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard?view=dispatch"
            aria-label="Open Dispatch"
            className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-[var(--wash)] font-bold text-[var(--ink)]"
          >
            {company.logoAttachmentId &&
            company.logoAvailability !== "not_copied" ? (
              <img
                src={`${API_URL}/api/v1/workspace/attachments/${company.logoAttachmentId}/content`}
                alt=""
                className="h-full w-full object-contain"
              />
            ) : (
              initials(company.name)
            )}
          </Link>
          <div>
            <p className="text-base font-extrabold text-[var(--ink)]">
              {company.name}
            </p>
            <p className="text-xs text-[var(--ink-soft)]">Company settings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="mailto:info@workviewpro.com"
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] transition hover:border-[var(--line)] hover:bg-[var(--wash)]"
          >
            Help &amp; Support
          </a>
          <Link
            to="/dashboard?view=dispatch"
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] transition hover:border-[var(--line)] hover:bg-[var(--wash)]"
          >
            ← Back to Dispatch
          </Link>
        </div>
      </header>
      <form
        onSubmit={save}
        className="mx-auto max-w-[1440px] px-5 py-8 sm:px-8"
      >
        <h1 className="mb-6 text-3xl font-semibold tracking-tight text-[var(--ink)]">
          Company settings
        </h1>
        <div
          role="tablist"
          aria-label="Company settings"
          className="mb-6 flex gap-1 overflow-x-auto border-b border-[var(--line)]"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedPanel(tab.id === "zones" ? "Zones" : null);
              }}
              className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition ${activeTab === tab.id ? "border-[var(--line)] text-[var(--ink)]" : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {error && (
          <div
            className="mb-6 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] px-4 py-3 text-sm text-[var(--accent)]"
            role="alert"
          >
            {error}
          </div>
        )}
        <SettingsSelection.Provider
          value={{ selected: selectedPanel, select: setSelectedPanel }}
        >
          <div
            role="tabpanel"
            aria-label={`${activeTab} company settings`}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {activeTab === "overview" && (
              <>
                <div className="contents">
                  <Panel title="Customer data">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                      <div>
                        <p className="text-3xl font-semibold tracking-tight text-[var(--ink)]">
                          {customerCount === null
                            ? "—"
                            : customerCount.toLocaleString("en-US")}
                        </p>
                        <p className="mt-1 text-sm text-[var(--ink-soft)]">
                          Customers imported from ServiceTitan
                        </p>
                      </div>
                      <Link
                        to="/dashboard?view=customers"
                        className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3.5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:bg-[var(--wash)]"
                      >
                        Search customers
                      </Link>
                    </div>
                  </Panel>
                  {(quickBooks ||
                    quickBooksUnavailable ||
                    quickBooksMessage) && (
                    <QuickBooksPanel
                      status={quickBooks}
                      unavailable={quickBooksUnavailable}
                      message={quickBooksMessage}
                      error={quickBooksError}
                      disconnecting={isDisconnectingQuickBooks}
                      setup={quickBooksSetup}
                      scanning={isScanningQuickBooks}
                      savingSetup={isSavingQuickBooksSetup}
                      savingBillMapping={isSavingQuickBooksBillMapping}
                      onConnect={connectQuickBooks}
                      onDisconnect={() => void disconnectQuickBooks()}
                      onConfirmRealmSwitch={() =>
                        void confirmQuickBooksRealmSwitch()
                      }
                      onCancelRealmSwitch={() =>
                        void cancelQuickBooksRealmSwitch()
                      }
                      onScan={() => void scanQuickBooksSetup()}
                      onSaveSetup={(mappings) =>
                        void saveQuickBooksSetup(mappings)
                      }
                      onSaveBillMapping={(apAccountId, expenseAccountId) =>
                        void saveQuickBooksBillMapping(
                          apAccountId,
                          expenseAccountId,
                        )
                      }
                      onSaveSourceAccountMappings={(mappings) =>
                        void saveQuickBooksSourceAccountMappings(mappings)
                      }
                      onCreateAccountingAccount={(accountKind, name) =>
                        void createQuickBooksAccountingAccount(
                          accountKind,
                          name,
                        )
                      }
                      onSaveCustomerDiscountAccount={(accountId) =>
                        void saveQuickBooksCustomerDiscountAccount(accountId)
                      }
                    />
                  )}
                  <TwilioPanel
                    status={twilioConnection}
                    companyPhone={company.phone}
                    jobTypes={data.jobTypes}
                    onSave={async (body) => {
                      const response = await apiFetch(
                        "/api/v1/workspace/company/integrations/twilio",
                        {
                          method: "PUT",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify(body),
                        },
                      );
                      const payload = (await response.json()) as {
                        data?: TwilioConnectionStatus;
                        error?: { message?: { message?: string } | string };
                      };
                      if (!response.ok || !payload.data)
                        throw new Error(
                          typeof payload.error?.message === "string"
                            ? payload.error.message
                            : "Unable to save Twilio connection.",
                        );
                      setTwilioConnection((current) => ({
                        ...payload.data!,
                        techLine: current?.techLine ?? null,
                        voiceBookingAgent: current?.voiceBookingAgent,
                        editable: true,
                      }));
                    }}
                    onSaveTechLine={async (body) => {
                      const response = await apiFetch(
                        "/api/v1/workspace/company/integrations/twilio/tech-line",
                        {
                          method: "PUT",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify(body),
                        },
                      );
                      const payload = (await response.json()) as {
                        data?: NonNullable<TwilioConnectionStatus["techLine"]>;
                        error?: { message?: string };
                      };
                      if (!response.ok || !payload.data)
                        throw new Error(
                          payload.error?.message ||
                            "Unable to save Tech Line settings.",
                        );
                      setTwilioConnection((current) => ({
                        ...(current ?? { connected: true, editable: true }),
                        techLine: payload.data,
                      }));
                    }}
                    onSaveTechLineDiagnostics={async (body) => {
                      const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/tech-line/diagnostics", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
                      const payload = (await response.json()) as { data?: { enabled: boolean; expiresAt: string | null }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to save diagnostics.");
                      setTwilioConnection((current) => current ? { ...current, techLineDiagnostics: payload.data } : current);
                      return payload.data;
                    }}
                    onLoadTechLineDiagnostics={async () => {
                      const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/tech-line/diagnostics/events");
                      const payload = (await response.json()) as { data?: { items?: Array<{ id: string; kind: string; transcript: string | null; detail: string | null; destinationName: string | null; createdAt: string; expiresAt: string }> }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to load diagnostics.");
                      return payload.data.items ?? [];
                    }}
                    onSaveVoiceBookingAgent={async (body) => {
                      const response = await apiFetch(
                        "/api/v1/workspace/company/integrations/twilio/voice-booking-agent",
                        {
                          method: "PUT",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify({
                            enabled: body.enabled,
                            emergencyForwardTo: body.emergencyForwardTo,
                            jobTypeIds: body.jobTypeIds,
                            recordingEnabled: body.recordingEnabled,
                            transcriptEnabled: body.transcriptEnabled,
                          }),
                        },
                      );
                      const payload = (await response.json()) as {
                        data?: VoiceBookingAgentSettings;
                        error?: { message?: string };
                      };
                      if (!response.ok || !payload.data)
                        throw new Error(
                          payload.error?.message ||
                            "Unable to save booking agent settings.",
                        );
                      setTwilioConnection((current) =>
                        current
                          ? { ...current, voiceBookingAgent: payload.data }
                          : current,
                      );
                    }}
                    onSaveOnMyWaySms={async (body) => {
                      const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/on-my-way-sms", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
                      const payload = (await response.json()) as { data?: { enabled: boolean; template: string; sampleRecipientPhone: string | null }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to save on-my-way text settings.");
                      setTwilioConnection((current) => current ? { ...current, onMyWaySms: payload.data } : current);
                    }}
                    onSendOnMyWaySample={async (body) => {
                      const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/on-my-way-sms/sample", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
                      const payload = (await response.json()) as { data?: { fromNumber: string }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to send sample text.");
                      return payload.data;
                    }}
                    onSaveAppointmentConfirmationSms={async (body) => {
                      const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/appointment-confirmation-sms", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
                      const payload = (await response.json()) as { data?: { enabled: boolean; template: string; sampleRecipientPhone: string | null }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to save appointment confirmation settings.");
                      setTwilioConnection((current) => current ? { ...current, appointmentConfirmationSms: payload.data! } : current);
                    }}
					onSendAppointmentConfirmationSample={async (body) => {
						const response = await apiFetch("/api/v1/workspace/company/integrations/twilio/appointment-confirmation-sms/sample", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
						const payload = await response.json() as { data?: { fromNumber: string }; error?: { message?: string } };
						if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to send sample text.");
						return payload.data;
					}}
                    onSaveOutboundCallRecipient={async (userId, phoneNumber) => {
                      const response = await apiFetch(`/api/v1/workspace/company/integrations/twilio/outbound-call-recipients/${encodeURIComponent(userId)}`, { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ phoneNumber }) });
                      const payload = (await response.json()) as { data?: { id: string; phoneNumber: string | null }; error?: { message?: string } };
                      if (!response.ok || !payload.data) throw new Error(payload.error?.message || "Unable to save the outbound call number.");
                      setTwilioConnection((current) => current ? { ...current, outboundCallRecipients: (current.outboundCallRecipients ?? []).map((recipient) => recipient.id === payload.data!.id ? { ...recipient, phoneNumber: payload.data!.phoneNumber } : recipient) } : current);
                    }}
                  />
                  <TechLineParticipantPanel
                    editable={Boolean(twilioConnection?.editable)}
                    team={team}
                    participants={twilioConnection?.techLineParticipants ?? []}
                    onSave={async (participant, next) => {
                      const response = await apiFetch(
                        `/api/v1/workspace/company/integrations/twilio/tech-line/participants/${encodeURIComponent(participant.id)}`,
                        {
                          method: "PUT",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify(next),
                        },
                      );
                      const payload = (await response.json()) as {
                        error?: { message?: string };
                      };
                      if (!response.ok)
                        throw new Error(
                          payload.error?.message ||
                            "Unable to update Tech Line access.",
                        );
                      setTwilioConnection((current) =>
                        current
                          ? {
                              ...current,
                              techLineParticipants: (
                                current.techLineParticipants ?? []
                              ).map((item) =>
                                item.id === participant.id
                                  ? { ...item, ...next }
                                  : item,
                              ),
                            }
                          : current,
                      );
                    }}
                  />
                  <StripePaymentsPanel
                    account={stripeAccount}
                    unavailable={stripeUnavailable}
                    busy={isStartingStripe}
                    openingDashboard={isOpeningStripeDashboard}
                    disconnecting={isDisconnectingStripe}
                    error={stripeError}
                    onStart={() => void startStripeOnboarding()}
                    onAdopt={(accountId) => void adoptStripeAccount(accountId)}
                    onOpenDashboard={() => void openStripeDashboard()}
                    onDisconnect={() => void disconnectStripe()}
                  />
                  <StripeTaxPanel
                    settings={stripeTaxSettings}
                    accountReady={Boolean(stripeAccount?.chargesEnabled)}
                    onSave={saveStripeTaxSettings}
                  />
                  <Panel title="Company logo" eyebrow="Identity">
                    <div className="flex items-center gap-4">
                      {company.logoAttachmentId &&
                      company.logoAvailability !== "not_copied" ? (
                        <img
                          src={`${API_URL}/api/v1/workspace/attachments/${company.logoAttachmentId}/content`}
                          alt={`${company.name} logo`}
                          className="h-16 w-16 rounded-xl border border-[var(--line)] object-cover"
                        />
                      ) : (
                        <span className="grid h-16 w-16 place-items-center rounded-xl bg-[var(--wash)] text-xl font-bold text-[var(--ink-soft)]">
                          {initials(company.name)}
                        </span>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <label className="cursor-pointer rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]">
                          Upload
                          <input
                            aria-label="Company logo"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="sr-only"
                            onChange={(event) =>
                              void uploadCompanyLogo(event.target.files?.[0])
                            }
                          />
                        </label>
                        {company.logoAttachmentId && (
                          <button
                            type="button"
                            onClick={() => void removeCompanyLogo()}
                            className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink-soft)]"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {company.logoAvailability === "not_copied" && (
                      <p className="mt-2 text-xs text-[var(--ink-faint)]">
                        Not copied to preview.
                      </p>
                    )}
                    <p className="mt-3 text-xs text-[var(--ink-faint)]">
                      JPEG, PNG, or WebP · up to 10 MiB.
                    </p>
                  </Panel>
                  <Panel title="Business information" eyebrow="Identity">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Business name"
                        value={form.name}
                        onChange={(value) => update("name", value)}
                        required
                      />
                      <Field
                        label="Legal name"
                        value={form.legalName}
                        onChange={(value) => update("legalName", value)}
                      />
                      <Field
                        label="Business email"
                        value={form.email}
                        onChange={(value) => update("email", value)}
                        type="email"
                        required
                      />
                      <Field
                        label="Phone"
                        value={form.phone}
                        onChange={(value) => update("phone", value)}
                      />
                      <Field
                        label="Tax number"
                        value={form.taxNumber}
                        onChange={(value) => update("taxNumber", value)}
                      />
                      <Field
                        label="Country"
                        value={form.country}
                        onChange={(value) => update("country", value)}
                      />
                    </div>
                    {businessTypes && (
                      <p className="mt-5 text-xs text-[var(--ink-faint)]">
                        {businessTypes}
                      </p>
                    )}
                  </Panel>
                  <Panel title="Business address" eyebrow="Contact">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Address line 1"
                        value={form.addressLine1}
                        onChange={(value) => update("addressLine1", value)}
                      />
                      <Field
                        label="Address line 2"
                        value={form.addressLine2}
                        onChange={(value) => update("addressLine2", value)}
                      />
                      <Field
                        label="City"
                        value={form.city}
                        onChange={(value) => update("city", value)}
                      />
                      <Field
                        label="State"
                        value={form.state}
                        onChange={(value) => update("state", value)}
                      />
                      <Field
                        label="Postal code"
                        value={form.postalCode}
                        onChange={(value) => update("postalCode", value)}
                      />
                    </div>
                  </Panel>
                </div>
              </>
            )}
            <div className={activeTab === "email" ? "contents" : "hidden"}>
              <EmailIdentityPanel
                company={company}
                form={form}
                onEdit={(panel) => {
                  setActiveTab("overview");
                  setSelectedPanel(panel);
                }}
              />
              {emailSettingsUnavailable && (
                <p
                  role="status"
                  className="rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3 text-sm"
                >
                  Email settings can be previewed here. Saving requires the
                  matching API update.
                </p>
              )}
              <EmailCustomizationPanel
                company={company}
                form={form}
                team={team}
                templates={emailTemplates}
                emailSettings={emailSettings}
                onSaveTemplate={saveEmailTemplate}
                onSaveSettings={saveEmailSettings}
              />
              <EmailDeliveryPanel team={team} />
            </div>
            {activeTab === "zones" && (
              <Panel title="Zones" eyebrow="Dispatch coverage">
                <ServiceAreasEditor
                  areas={form.serviceAreas}
                  onChange={updateServiceArea}
                  onAdd={addServiceArea}
                />
              </Panel>
            )}
            {activeTab === "operations" && (
              <>
                <div className="contents">
                  <PurchaseOrderTypeSettings />
                  <Panel
                    title="Operating settings"
                    eyebrow="Workspace defaults"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <label className="block sm:col-span-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                          Timezone
                        </span>
                        <select
                          value={form.timezone}
                          onChange={(event) =>
                            update("timezone", event.target.value)
                          }
                          className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2.5 text-sm text-[var(--ink)] outline-none focus:border-[var(--line)] focus:bg-[var(--paper)]"
                        >
                          {timezones.map((timezone) => (
                            <option key={timezone}>{timezone}</option>
                          ))}
                          {!timezones.includes(form.timezone) && (
                            <option>{form.timezone}</option>
                          )}
                        </select>
                      </label>
                      <label className="flex items-center justify-between gap-4 rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2.5 sm:col-span-2">
                        <span>
                          <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                            Emergency service
                          </span>
                          <span className="mt-1 block text-sm text-[var(--ink-soft)]">
                            Accept emergency work
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          checked={form.emergencyServiceAvailable}
                          onChange={(event) =>
                            update(
                              "emergencyServiceAvailable",
                              event.target.checked,
                            )
                          }
                          className="h-5 w-5 accent-[var(--ink)]"
                        />
                      </label>
                    </div>
                  </Panel>
                  <Panel title="After-hours window" eyebrow="Pricing">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Starts at"
                        type="time"
                        value={form.afterHoursStartsAt}
                        onChange={(value) =>
                          update("afterHoursStartsAt", value)
                        }
                        required
                      />
                      <Field
                        label="Ends at"
                        type="time"
                        value={form.afterHoursEndsAt}
                        onChange={(value) => update("afterHoursEndsAt", value)}
                        required
                      />
                    </div>
                    <p className="mt-3 text-sm text-[var(--ink-soft)]">
                      Jobs scheduled in this window are priced with the
                      after-hours rule selected on the job. The window can cross
                      midnight.
                    </p>
                  </Panel>
                  <Panel title="Business hours" eyebrow="Schedule">
                    <div className="space-y-3">
                      {form.businessHours.map((hours) => (
                        <div
                          key={hours.dayOfWeek}
                          className="grid gap-2 border-b border-[var(--line)] pb-3 last:border-0 sm:grid-cols-[1fr_auto_auto] sm:items-center"
                        >
                          <label className="flex items-center gap-2 text-sm font-medium text-[var(--ink-soft)]">
                            <input
                              type="checkbox"
                              checked={!hours.isClosed}
                              onChange={(event) =>
                                updateHour(hours.dayOfWeek, {
                                  isClosed: !event.target.checked,
                                  opensAt: event.target.checked
                                    ? hours.opensAt || "07:00"
                                    : null,
                                  closesAt: event.target.checked
                                    ? hours.closesAt || "17:00"
                                    : null,
                                })
                              }
                              className="h-4 w-4 accent-[var(--ink)]"
                            />
                            {days[hours.dayOfWeek]}
                          </label>
                          {hours.isClosed ? (
                            <span className="text-sm text-[var(--ink-faint)] sm:col-span-2">
                              Closed
                            </span>
                          ) : (
                            <>
                              <input
                                aria-label={`${days[hours.dayOfWeek]} opens`}
                                type="time"
                                value={hours.opensAt || "07:00"}
                                onChange={(event) =>
                                  updateHour(hours.dayOfWeek, {
                                    opensAt: event.target.value,
                                  })
                                }
                                className="rounded-lg border border-[var(--line)] bg-[var(--wash)] px-2 py-2 text-sm"
                              />
                              <input
                                aria-label={`${days[hours.dayOfWeek]} closes`}
                                type="time"
                                value={hours.closesAt || "17:00"}
                                onChange={(event) =>
                                  updateHour(hours.dayOfWeek, {
                                    closesAt: event.target.value,
                                  })
                                }
                                className="rounded-lg border border-[var(--line)] bg-[var(--wash)] px-2 py-2 text-sm"
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>
                <div className="contents">
                  <Panel title="Material markup" eyebrow="Pricing">
                    <Field
                      label="Markup percent"
                      type="number"
                      value={form.materialMarkupPercent}
                      onChange={(value) =>
                        update("materialMarkupPercent", value)
                      }
                    />
                    <p className="mt-3 text-sm text-[var(--ink-soft)]">
                      When a material item has a cost, its document price uses
                      cost plus this markup. Leave blank to use its regular
                      price.
                    </p>
                  </Panel>
                  <Panel
                    title="After-hours labor by job type"
                    eyebrow="Pricing"
                  >
                    <div className="space-y-3">
                      {form.jobTypeMultipliers
                        .filter((jobType) => jobType.active)
                        .map((jobType) => (
                          <label
                            key={jobType.id}
                            className="grid gap-2 sm:grid-cols-[1fr_150px] sm:items-end"
                          >
                            <span className="text-sm font-medium text-[var(--ink)]">
                              {jobType.name}
                            </span>
                            <input
                              aria-label={`${jobType.name} after-hours multiplier`}
                              type="number"
                              min="0.01"
                              max="10"
                              step="0.01"
                              value={jobType.multiplier}
                              placeholder="Regular price"
                              onChange={(event) =>
                                update(
                                  "jobTypeMultipliers",
                                  form.jobTypeMultipliers.map((item) =>
                                    item.id === jobType.id
                                      ? {
                                          ...item,
                                          multiplier: event.target.value,
                                        }
                                      : item,
                                  ),
                                )
                              }
                              className="rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2 text-sm"
                            />
                          </label>
                        ))}
                      {!form.jobTypeMultipliers.filter(
                        (jobType) => jobType.active,
                      ).length && (
                        <p className="text-sm text-[var(--ink-soft)]">
                          Create a job type when booking work to set its
                          after-hours labor multiplier.
                        </p>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-[var(--ink-soft)]">
                      For example, 1.5 charges labor at 150% when its selected
                      appointment is after hours. Leave blank to use the regular
                      labor price.
                    </p>
                  </Panel>
                  <Panel title="Current defaults" eyebrow="Summary">
                    <p className="text-sm leading-6 text-[var(--ink-soft)]">
                      These settings guide scheduling, dispatch availability,
                      material pricing, and emergency-work handling across the
                      workspace.
                    </p>
                  </Panel>
                </div>
              </>
            )}
            {activeTab === "memberships" && <MembershipSettingsPanel />}
            {activeTab === "operations" && (
              <div className="contents">
                <Panel title="Dispatch booking window">
                  <div className="grid max-w-xl gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                        Default duration
                      </span>
                      <select
                        aria-label="Default booking duration"
                        value={form.defaultBookingDurationMinutes}
                        onChange={(event) =>
                          update(
                            "defaultBookingDurationMinutes",
                            Number(event.target.value),
                          )
                        }
                        className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-[13px]"
                      >
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={90}>90 minutes</option>
                        <option value={120}>2 hours</option>
                        <option value={180}>3 hours</option>
                        <option value={240}>4 hours</option>
                      </select>
                    </label>
                    <Field
                      label="Booking horizon (days)"
                      type="number"
                      value={String(form.bookingHorizonDays)}
                      onChange={(value) =>
                        update("bookingHorizonDays", Number(value))
                      }
                    />
                    <Field
                      label="Earliest start"
                      type="time"
                      value={form.earliestBookingStart}
                      onChange={(value) =>
                        update("earliestBookingStart", value)
                      }
                    />
                    <Field
                      label="Latest start"
                      type="time"
                      value={form.latestBookingStart}
                      onChange={(value) => update("latestBookingStart", value)}
                    />
                    <Field
                      label="Lunch starts"
                      type="time"
                      value={form.lunchStartsAt}
                      onChange={(value) => update("lunchStartsAt", value)}
                    />
                    <Field
                      label="Lunch ends"
                      type="time"
                      value={form.lunchEndsAt}
                      onChange={(value) => update("lunchEndsAt", value)}
                    />
                  </div>
                  <p className="mt-3 text-sm text-[var(--ink-soft)]">
                    Lunch overlap is shown as a warning when booking; it does
                    not block the office.
                  </p>
                </Panel>
              </div>
            )}
            {activeTab === "team" && (
              <div className="contents">
                <Panel title={`Team (${visibleTeam.length})`}>
                  <div className="space-y-2">
                    {visibleTeam.map((member) => (
                      <TeamRow
                        key={member.id}
                        member={member}
                        busy={memberUpdating === member.id}
                        openProfile={openProfile}
                        openRoleEditor={openRoleEditor}
                        setMemberActive={(selected, active) =>
                          void setMemberActive(selected, active)
                        }
                        resendSetup={resendSetup}
                        removeMember={(selected) => void removeMember(selected)}
                      />
                    ))}
                  </div>
                  <div
                    role="group"
                    aria-label="Invite office user"
                    className="mt-4 space-y-2 border-t border-[var(--line)] pt-4"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                      Invite an office user
                    </span>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <input
                        value={inviteName}
                        onChange={(event) => setInviteName(event.target.value)}
                        placeholder="Name"
                        className="rounded-lg border border-[var(--line)] px-2.5 py-2 text-[13px]"
                      />
                      <input
                        value={inviteEmail}
                        onChange={(event) => setInviteEmail(event.target.value)}
                        type="email"
                        placeholder="Work email"
                        className="rounded-lg border border-[var(--line)] px-2.5 py-2 text-[13px]"
                      />
                      <select
                        aria-label="Invited user role"
                        value={inviteRole}
                        onChange={(event) =>
                          setInviteRole(
                            event.target.value as "Admin" | "Dispatcher",
                          )
                        }
                        className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2.5 py-2 text-[13px]"
                      >
                        <option>Dispatcher</option>
                        <option>Admin</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      disabled={!inviteName.trim() || !inviteEmail.trim()}
                      onClick={() => void inviteUser()}
                      className="min-h-10 w-full rounded-lg bg-[var(--ink)] text-[13px] font-semibold text-[var(--paper)] disabled:opacity-50"
                    >
                      Invite user
                    </button>
                  </div>
                  <div
                    role="group"
                    aria-label="Add technician"
                    className="mt-4 space-y-2 border-t border-[var(--line)] pt-4"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                      Add a technician
                    </span>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <input
                        value={technicianName}
                        onChange={(event) =>
                          setTechnicianName(event.target.value)
                        }
                        placeholder="Name"
                        className="rounded-lg border border-[var(--line)] px-2.5 py-2 text-[13px]"
                      />
                      <input
                        value={technicianEmail}
                        onChange={(event) =>
                          setTechnicianEmail(event.target.value)
                        }
                        type="email"
                        placeholder="Work email"
                        className="rounded-lg border border-[var(--line)] px-2.5 py-2 text-[13px]"
                      />
                      <input
                        value={technicianPhone}
                        onChange={(event) =>
                          setTechnicianPhone(event.target.value)
                        }
                        placeholder="Phone"
                        className="rounded-lg border border-[var(--line)] px-2.5 py-2 text-[13px]"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={
                        !technicianName.trim() || !technicianEmail.trim()
                      }
                      onClick={() => void addTechnician()}
                      className="min-h-10 w-full rounded-lg bg-[var(--ink)] text-[13px] font-semibold text-[var(--paper)] disabled:opacity-50"
                    >
                      Add technician
                    </button>
                  </div>
                  {teamError && (
                    <p
                      role="alert"
                      className="mt-3 text-[13px] font-semibold text-[var(--accent)]"
                    >
                      {teamError}
                    </p>
                  )}
                  {teamMessage && (
                    <p
                      role="status"
                      className="mt-3 text-[13px] font-semibold text-[var(--ink-soft)]"
                    >
                      {teamMessage}
                    </p>
                  )}
                  {inactiveCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowInactive(!showInactive)}
                      className="mt-4 text-xs font-semibold text-[var(--accent)]"
                    >
                      {showInactive ? "Hide" : "Show"} {inactiveCount} inactive
                    </button>
                  )}
                </Panel>
                <Panel title="Skills" eyebrow="Dispatch">
                  <div className="flex gap-2">
                    <input
                      value={skillName}
                      onChange={(event) => setSkillName(event.target.value)}
                      placeholder="Add a skill"
                      className="min-w-0 flex-1 rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => void createSkill()}
                      className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
                    >
                      Add
                    </button>
                  </div>
                  {skillError && (
                    <p className="mt-3 text-sm text-[var(--accent)]">
                      {skillError}
                    </p>
                  )}
                  <div className="mt-4 space-y-2">
                    {data.skills.map((skill) => (
                      <div
                        key={skill.id}
                        className="flex items-center justify-between gap-2 rounded-lg bg-[var(--wash)] px-3 py-2"
                      >
                        <span
                          className={`text-sm font-medium ${skill.active ? "text-[var(--ink)]" : "text-[var(--ink-faint)] line-through"}`}
                        >
                          {skill.name}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const name = window.prompt(
                                "Skill name",
                                skill.name,
                              );
                              if (name?.trim() && name !== skill.name)
                                void updateSkill(skill.id, { name });
                            }}
                            className="text-xs font-semibold text-[var(--accent)]"
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              void updateSkill(skill.id, {
                                active: !skill.active,
                              })
                            }
                            className="text-xs font-semibold text-[var(--ink-soft)]"
                          >
                            {skill.active ? "Archive" : "Restore"}
                          </button>
                        </div>
                      </div>
                    ))}
                    {!data.skills.length && (
                      <p className="text-sm text-[var(--ink-faint)]">
                        No skills yet.
                      </p>
                    )}
                  </div>
                </Panel>
              </div>
            )}
            {activeTab === "operations" && (
              <section className="contents">
                <Panel title="Job number display">
                  <Field
                    label="Native job number visual offset"
                    type="number"
                    value={form.jobNumberDisplayOffset}
                    onChange={(value) =>
                      update("jobNumberDisplayOffset", value)
                    }
                  />
                  <p className="mt-3 text-sm text-[var(--ink-soft)]">
                    For example, 50,000 displays internal #100001 as #50001.
                    ServiceTitan job numbers are unchanged.
                  </p>
                </Panel>
                <Panel title="Estimate number display">
                  <Field
                    label="Estimate prefix"
                    value={form.estimateNumberPrefix}
                    onChange={(value) => update("estimateNumberPrefix", value)}
                  />
                  <Field
                    label="Next estimate number"
                    type="number"
                    value={form.estimateNumberNext}
                    onChange={(value) => update("estimateNumberNext", value)}
                  />
                  <p className="mt-3 text-sm text-[var(--ink-soft)]">
                    New estimates use this prefix and number only. For example, prefix E- and next number 26000 creates E-26000. Existing estimates are unchanged.
                  </p>
                </Panel>
                <Panel title="Manage job types">
                  <div className="flex gap-2">
                    <input
                      aria-label="New job type"
                      value={jobTypeName}
                      onChange={(event) => setJobTypeName(event.target.value)}
                      placeholder="e.g. General service"
                      className="min-w-0 flex-1 rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => void createJobType()}
                      className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
                    >
                      Add
                    </button>
                  </div>
                  {jobTypeError && (
                    <p
                      role="alert"
                      className="mt-3 text-sm text-[var(--accent)]"
                    >
                      {jobTypeError}
                    </p>
                  )}
                  <div className="mt-4 space-y-2">
                    {data.jobTypes.map((jobType) => (
                      <div
                        key={jobType.id}
                        className="rounded-lg bg-[var(--wash)] px-3 py-2"
                      >
                        {editingJobTypeId === jobType.id ? (
                          <div className="space-y-3">
                            <input
                              aria-label="Job type name"
                              value={editingJobTypeName}
                              onChange={(event) =>
                                setEditingJobTypeName(event.target.value)
                              }
                              className="min-w-0 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-1 text-sm"
                            />
                            <div>
                              <p className="text-xs font-semibold text-[var(--ink-soft)]">
                                Default required skills
                              </p>
                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                                {data.skills
                                  .filter((skill) => skill.active)
                                  .map((skill) => (
                                    <label
                                      key={skill.id}
                                      className="flex items-center gap-1.5 text-sm font-medium text-[var(--ink)]"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={editingJobTypeSkillIds.includes(
                                          skill.id,
                                        )}
                                        onChange={(event) =>
                                          setEditingJobTypeSkillIds((ids) =>
                                            event.target.checked
                                              ? [...ids, skill.id]
                                              : ids.filter(
                                                  (id) => id !== skill.id,
                                                ),
                                          )
                                        }
                                      />
                                      {skill.name}
                                    </label>
                                  ))}
                              </div>
                              {!data.skills.some((skill) => skill.active) && (
                                <p className="mt-2 text-xs text-[var(--ink-faint)]">
                                  Add active company skills before setting
                                  defaults.
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={!editingJobTypeName.trim()}
                                onClick={() => {
                                  void updateJobType(jobType.id, {
                                    name: editingJobTypeName.trim(),
                                    requiredSkillIds: editingJobTypeSkillIds,
                                  });
                                  setEditingJobTypeId(null);
                                }}
                                className="text-xs font-semibold text-[var(--accent)] disabled:opacity-50"
                              >
                                Save defaults
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingJobTypeId(null)}
                                className="text-xs font-semibold text-[var(--ink-soft)]"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <span
                                className={`text-sm font-medium ${jobType.active ? "text-[var(--ink)]" : "text-[var(--ink-faint)] line-through"}`}
                              >
                                {jobType.name}
                              </span>
                              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                                {jobType.requiredSkillIds.length
                                  ? `${jobType.requiredSkillIds.length} default required skill${jobType.requiredSkillIds.length === 1 ? "" : "s"}`
                                  : "No default required skills"}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingJobTypeId(jobType.id);
                                  setEditingJobTypeName(jobType.name);
                                  setEditingJobTypeSkillIds(
                                    jobType.requiredSkillIds,
                                  );
                                }}
                                className="text-xs font-semibold text-[var(--accent)]"
                              >
                                Edit defaults
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  void updateJobType(jobType.id, {
                                    active: !jobType.active,
                                  })
                                }
                                className="text-xs font-semibold text-[var(--ink-soft)]"
                              >
                                {jobType.active ? "Archive" : "Restore"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {!data.jobTypes.length && (
                      <p className="text-sm text-[var(--ink-faint)]">
                        No job types yet. Add one before creating a priced
                        document.
                      </p>
                    )}
                  </div>
                  <div className="mt-5 border-t border-[var(--line)] pt-4">
                    <button
                      type="button"
                      onClick={() => void reviewJobSkillDefaults()}
                      className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--ink)]"
                    >
                      Review open-job default candidates
                    </button>
                    {jobSkillDefaultsPreviewError && (
                      <p
                        role="alert"
                        className="mt-2 text-sm font-semibold text-[var(--accent)]"
                      >
                        {jobSkillDefaultsPreviewError}
                      </p>
                    )}
                    {jobSkillDefaultsPreview && (
                      <div className="mt-3 space-y-2 text-sm text-[var(--ink-soft)]">
                        <p>
                          {jobSkillDefaultsPreview.candidates.length
                            ? jobSkillDefaultsPreview.candidates
                                .map(
                                  (item) => `${item.count} ${item.jobTypeName}`,
                                )
                                .join(" · ")
                            : "No eligible open jobs."}
                        </p>
                        <p>
                          Skipped:{" "}
                          {jobSkillDefaultsPreview.exceptions.noPrimaryType}{" "}
                          without a primary type,{" "}
                          {
                            jobSkillDefaultsPreview.exceptions
                              .inactivePrimaryType
                          }{" "}
                          with an archived type,{" "}
                          {jobSkillDefaultsPreview.exceptions.noDefaults} with
                          no defaults,{" "}
                          {
                            jobSkillDefaultsPreview.exceptions
                              .existingRequirements
                          }{" "}
                          with existing skills, and{" "}
                          {jobSkillDefaultsPreview.exceptions.alreadySet}{" "}
                          already set.
                        </p>
                        <p className="text-xs">
                          {jobSkillDefaultsPreview.warning}
                        </p>
                      </div>
                    )}
                  </div>
                </Panel>
                <Panel title="Timer-based labor billing" eyebrow="Invoices">
                  <p className="mb-3 text-sm text-[var(--ink-soft)]">
                    Automatically add one labor line from technician timers.
                    Manual edits remain an override until reset from the
                    invoice.
                  </p>
                  <div className="space-y-3">
                    {data.jobTypes
                      .filter((jobType) => jobType.active)
                      .map((jobType) => {
                        const itemId =
                          jobType.laborPriceBookItemId ??
                          data.laborPricebookItems?.[0]?.id ??
                          "";
                        const minimum = jobType.laborMinimumMinutes ?? 60;
                        const increment =
                          jobType.laborRoundingIncrementMinutes ?? 30;
                        return (
                          <div
                            key={jobType.id}
                            className="rounded-lg bg-[var(--wash)] p-3"
                          >
                            <label className="flex items-center justify-between gap-3 text-sm font-medium">
                              <span>{jobType.name}</span>
                              <input
                                type="checkbox"
                                checked={Boolean(jobType.laborBillingEnabled)}
                                onChange={(event) =>
                                  void saveLaborBilling(
                                    jobType,
                                    event.target.checked,
                                    itemId || null,
                                    minimum,
                                    increment,
                                  )
                                }
                              />
                            </label>
                            {jobType.laborBillingEnabled && (
                              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                <select
                                  aria-label={`${jobType.name} labor item`}
                                  defaultValue={itemId}
                                  onChange={(event) =>
                                    void saveLaborBilling(
                                      jobType,
                                      true,
                                      event.target.value,
                                      minimum,
                                      increment,
                                    )
                                  }
                                  className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm"
                                >
                                  {data.laborPricebookItems?.map((item) => (
                                    <option key={item.id} value={item.id}>
                                      {item.name} · $
                                      {(item.priceCents / 100).toFixed(2)}/hr
                                    </option>
                                  ))}
                                </select>
                                <input
                                  aria-label={`${jobType.name} minimum minutes`}
                                  defaultValue={minimum}
                                  type="number"
                                  min="1"
                                  onBlur={(event) =>
                                    void saveLaborBilling(
                                      jobType,
                                      true,
                                      itemId,
                                      Number(event.target.value),
                                      increment,
                                    )
                                  }
                                  className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm"
                                />
                                <input
                                  aria-label={`${jobType.name} rounding minutes`}
                                  defaultValue={increment}
                                  type="number"
                                  min="1"
                                  onBlur={(event) =>
                                    void saveLaborBilling(
                                      jobType,
                                      true,
                                      itemId,
                                      minimum,
                                      Number(event.target.value),
                                    )
                                  }
                                  className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-2 py-2 text-sm"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  {!data.laborPricebookItems?.length && (
                    <p className="mt-3 text-sm text-[var(--accent)]">
                      Create an active hourly labor item in Pricebook before
                      enabling this rule.
                    </p>
                  )}
                </Panel>
                <Panel title="Pricing data readiness">
                  {pricingReadiness ? (
                    <div className="space-y-3 text-sm text-[var(--ink-soft)]">
                      <p>
                        <strong className="text-[var(--ink)]">
                          {pricingReadiness.jobsWithoutJobType}
                        </strong>{" "}
                        existing job
                        {pricingReadiness.jobsWithoutJobType === 1 ? "" : "s"}{" "}
                        without a job type. Assign one from each job before
                        pricing documents.
                      </p>
                      <p>
                        <strong className="text-[var(--ink)]">
                          {pricingReadiness.activePricebookItems}
                        </strong>{" "}
                        active Pricebook item
                        {pricingReadiness.activePricebookItems === 1 ? "" : "s"}
                        , including {pricingReadiness.activeMaterialItems}{" "}
                        material
                        {pricingReadiness.activeMaterialItems === 1 ? "" : "s"}.
                      </p>
                      <p>
                        <strong className="text-[var(--ink)]">
                          {pricingReadiness.materialsWithoutCost}
                        </strong>{" "}
                        active material item
                        {pricingReadiness.materialsWithoutCost === 1 ? "" : "s"}{" "}
                        without a cost. Material markup cannot apply until a
                        cost is entered.
                      </p>
                      <p>
                        <strong className="text-[var(--ink)]">
                          {pricingReadiness.zeroPriceItems}
                        </strong>{" "}
                        active item
                        {pricingReadiness.zeroPriceItems === 1
                          ? " has"
                          : "s have"}{" "}
                        a $0 customer price; review the items that should bill a
                        charge.
                      </p>
                      {pricingReadiness.uncategorizedPricebookItems > 0 && (
                        <p>
                          <strong className="text-[var(--ink)]">
                            {pricingReadiness.uncategorizedPricebookItems}
                          </strong>{" "}
                          active item
                          {pricingReadiness.uncategorizedPricebookItems === 1
                            ? " needs"
                            : "s need"}{" "}
                          a Pricebook category.
                        </p>
                      )}
                      <p>
                        {pricingReadiness.businessHoursConfigured
                          ? "Business hours are configured for all seven days."
                          : "Business hours are incomplete. Set hours before relying on dispatch availability."}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--ink-soft)]">
                      The readiness report is temporarily unavailable.
                    </p>
                  )}
                </Panel>
                {Array.isArray(data.tags) && (
                  <Panel title="Tags">
                    <div className="flex gap-2">
                      <input
                        aria-label="New tag"
                        value={tagName}
                        onChange={(event) => setTagName(event.target.value)}
                        placeholder="e.g. VIP"
                        className="min-w-0 flex-1 rounded-lg border border-[var(--line)] px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => void createTag()}
                        className="rounded-lg bg-[var(--ink)] px-3 py-2 text-sm font-semibold text-[var(--paper)]"
                      >
                        Add
                      </button>
                    </div>
                    {tagError && (
                      <p
                        role="alert"
                        className="mt-3 text-sm text-[var(--accent)]"
                      >
                        {tagError}
                      </p>
                    )}
                    <div className="mt-4 space-y-2">
                      {data.tags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center justify-between gap-2 rounded-lg bg-[var(--wash)] px-3 py-2"
                        >
                          <span
                            className={`text-sm font-medium ${tag.active ? "text-[var(--ink)]" : "text-[var(--ink-faint)] line-through"}`}
                          >
                            {tag.name}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              void updateTag(tag.id, { active: !tag.active })
                            }
                            className="text-xs font-semibold text-[var(--ink-soft)]"
                          >
                            {tag.active ? "Archive" : "Restore"}
                          </button>
                        </div>
                      ))}
                      {!data.tags.length && (
                        <p className="text-sm text-[var(--ink-faint)]">
                          No tags yet.
                        </p>
                      )}
                    </div>
                  </Panel>
                )}
              </section>
            )}
          </div>
        </SettingsSelection.Provider>
        {activeTab !== "email" && activeTab !== "memberships" && (
          <div className="sticky bottom-0 mt-8 flex flex-wrap items-center justify-end gap-4 border-t border-[var(--line)] bg-[var(--wash)]/95 py-4 backdrop-blur">
            <p
              className="mr-auto text-sm font-medium text-[var(--ink)]"
              role="status"
            >
              {saveMessage}
            </p>
            <Link
              to="/dashboard"
              className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-2.5 text-sm font-semibold text-[var(--ink-soft)]"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-[var(--paper)] transition hover:bg-[var(--ink)] disabled:cursor-wait disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </form>
      {profileMember && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              setProfileMember(null);
              setTechnicianProfile(null);
            }}
            className="absolute inset-0 bg-[var(--ink)]/40"
          />
          <section
            role="dialog"
            aria-modal="true"
            className="relative flex max-h-[86vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]"
          >
            <header className="flex items-center justify-between gap-2 bg-[var(--wash)] px-3 py-2">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                {profileMember.name}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setProfileMember(null);
                  setTechnicianProfile(null);
                }}
                aria-label="Close"
                className="rounded-lg px-1.5 text-lg leading-none text-[var(--ink-soft)] hover:bg-[var(--paper)]/60"
              >
                ×
              </button>
            </header>
            <div className="overflow-y-auto p-3">
              {technicianProfile ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <>
                      {profileMember.photoAttachmentId ? (
                        <img
                          src={`${API_URL}/api/v1/workspace/attachments/${profileMember.photoAttachmentId}/content`}
                          alt=""
                          className="h-14 w-14 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="grid h-14 w-14 place-items-center rounded-lg bg-[var(--wash)] text-sm font-semibold text-[var(--ink-soft)]">
                          {initials(profileMember.name)}
                        </span>
                      )}
                    </>
                    <div className="flex flex-wrap gap-2">
                      <label className="cursor-pointer rounded-lg border border-[var(--line)] px-3 py-2 text-xs font-semibold text-[var(--ink-soft)]">
                        Upload photo
                        <input
                          aria-label="Technician photo"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="sr-only"
                          onChange={(event) =>
                            void uploadTechnicianPhoto(event.target.files?.[0])
                          }
                        />
                      </label>
                      {profileMember.photoAttachmentId && (
                        <button
                          type="button"
                          onClick={() => void removeTechnicianPhoto()}
                          className="rounded-lg border border-[var(--line)] px-3 py-2 text-xs font-semibold text-[var(--ink-soft)]"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <Field
                    label="Name"
                    value={technicianProfile.name}
                    onChange={(name) =>
                      setTechnicianProfile({ ...technicianProfile, name })
                    }
                    required
                  />
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field
                      label="Work email"
                      value={technicianProfile.email}
                      onChange={(email) =>
                        setTechnicianProfile({ ...technicianProfile, email })
                      }
                      type="email"
                      required
                    />
                    <Field
                      label="Phone"
                      value={technicianProfile.phone}
                      onChange={(phone) =>
                        setTechnicianProfile({ ...technicianProfile, phone })
                      }
                    />
                  </div>
                  <div className="space-y-2 border-t border-[var(--line)] pt-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                      Dispatch
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          setTechnicianProfile({
                            ...technicianProfile,
                            active: !technicianProfile.active,
                          })
                        }
                        className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${technicianProfile.active ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--wash)] text-[var(--ink-soft)]"}`}
                      >
                        Working here
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setTechnicianProfile({
                            ...technicianProfile,
                            dispatchable: !technicianProfile.dispatchable,
                          })
                        }
                        className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${technicianProfile.dispatchable ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--wash)] text-[var(--ink-soft)]"}`}
                      >
                        Show on the board
                      </button>
                    </div>
                  </div>
                  {technicianProfile.showCostRate && (
                    <Field
                      label="Hourly cost rate"
                      type="number"
                      value={
                        technicianProfile.costRateCents == null
                          ? ""
                          : String(technicianProfile.costRateCents / 100)
                      }
                      onChange={(value) =>
                        setTechnicianProfile({
                          ...technicianProfile,
                          costRateCents:
                            value.trim() === ""
                              ? null
                              : Math.round(Number(value) * 100),
                        })
                      }
                    />
                  )}
                  <div className="space-y-2 border-t border-[var(--line)] pt-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                      Skills
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {data.skills
                        .filter((skill) => skill.active)
                        .map((skill) => {
                          const on = technicianProfile.skillIds.includes(
                            skill.id,
                          );
                          return (
                            <button
                              key={skill.id}
                              type="button"
                              onClick={() =>
                                setTechnicianProfile({
                                  ...technicianProfile,
                                  skillIds: on
                                    ? technicianProfile.skillIds.filter(
                                        (id) => id !== skill.id,
                                      )
                                    : [...technicianProfile.skillIds, skill.id],
                                })
                              }
                              className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${on ? "bg-[var(--wash)] text-[var(--accent)] ring-1 ring-[var(--line-strong)]" : "bg-[var(--wash)] text-[var(--ink-soft)]"}`}
                            >
                              {skill.name}
                            </button>
                          );
                        })}
                      {!data.skills.some((skill) => skill.active) && (
                        <p className="text-[13px] text-[var(--ink-faint)]">
                          No skills yet — add them on the Team tab.
                        </p>
                      )}
                    </div>
                  </div>
                  {profileError && (
                    <p
                      role="alert"
                      className="text-[13px] font-semibold text-[var(--accent)]"
                    >
                      {profileError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => void saveProfile()}
                    className="min-h-10 w-full rounded-lg bg-[var(--ink)] text-[13px] font-semibold text-[var(--paper)]"
                  >
                    Save profile
                  </button>
                </div>
              ) : (
                <p className="text-[13px] text-[var(--ink-soft)]">Loading…</p>
              )}
            </div>
          </section>
        </div>
      )}
      {editingMember && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-[var(--ink)]/40 px-5"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEditingMember(null);
          }}
        >
          <section
            className="w-full max-w-md rounded-lg bg-[var(--paper)] p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-member-role-title"
          >
            <div className="flex items-start justify-between gap-4">
              <h2
                id="edit-member-role-title"
                className="text-lg font-semibold text-[var(--ink)]"
              >
                {editingMember.name}
              </h2>
              <button
                type="button"
                aria-label="Close edit role dialog"
                onClick={() => setEditingMember(null)}
                className="rounded-lg px-2 py-1 text-xl leading-none text-[var(--ink-faint)] hover:bg-[var(--wash)] hover:text-[var(--ink-soft)]"
              >
                ×
              </button>
            </div>
            <label className="mt-6 block">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
                Role
              </span>
              <select
                value={editingRole}
                onChange={(event) => setEditingRole(event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2.5 text-sm text-[var(--ink)] outline-none focus:border-[var(--line)] focus:bg-[var(--paper)]"
              >
                {data?.roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
            {roleError && (
              <p
                className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {roleError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingMember(null)}
                className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-2.5 text-sm font-semibold text-[var(--ink-soft)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveRole()}
                disabled={roleSaving || !editingRole}
                className="rounded-lg bg-[var(--ink)] px-4 py-2.5 text-sm font-semibold text-[var(--paper)] disabled:cursor-wait disabled:opacity-60"
              >
                {roleSaving ? "Saving…" : "Save role"}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
